/**
 * CardSettingsModal - Interaction mode + landing page settings
 * Extracted from Cards.jsx for maintainability.
 * 
 * Props:
 * - open, onOpenChange: Dialog state
 * - selectedCard: The card being configured
 * - state: All settings state values
 * - handlers: All state setters and action handlers
 * - refs: logoInputRef, adImageInputRef
 * - isSuperAdmin: Boolean function
 * - t: Translation function
 */
import { useMemo, useRef, useState, useEffect } from 'react';
import {
    Shield, Image, Upload, X, Images, Loader2, Clock, Calendar, Copy, ExternalLink, Plus,
    Mail, MessageSquare, Settings, ChevronUp, Globe, Film, Play, Volume2, VolumeX, SkipForward, Gift, Phone, Star, CheckCircle2, Megaphone, Hotel, Car, Layers, ShoppingBasket, Stethoscope, BadgeCheck
} from 'lucide-react';
import { CardPromoPanel } from '../promos/CardPromoPanel';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { LiveBrandingPreview } from '../shared/LiveBrandingPreview';
import { HelpButton } from '../docs/HelpButton';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import EnabledLanguagesPicker from '../shared/EnabledLanguagesPicker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { QuestionManager } from './QuestionManager';
import { TimingPanel } from './TimingPanel';
import { normalizePhoneE164 } from '../../lib/phoneUtils';
import api from '../../lib/api';

const TIMEZONES = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Toronto', 'America/Montreal', 'America/Vancouver', 'Europe/London',
    'Europe/Paris', 'Asia/Tokyo'
];

const DAYS_OF_WEEK = [
    { value: 'monday', label: 'Mon' }, { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' }, { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' }, { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
];

/**
 * P_TAP_PROTECTION_1.0 — Card-settings section for the umbrella
 * Tap Protection feature. Renders a master toggle + one nested
 * subsection per protection module.
 *
 * Design goals:
 *   1. An administrator should be able to answer these questions in
 *      < 30 seconds by looking at this section:
 *        - Is Tap Protection enabled?
 *        - Is Duplicate Protection enabled?
 *        - Is Dwell Protection enabled?
 *        - Does Dwell Protection apply to this card?
 *   2. Every rendered value is *actually* enforced by the runtime.
 *   3. The Dwell subsection is disabled + explained on Direct Tap
 *      cards — never hidden, never silently ignored.
 */
const TapProtectionSection = ({
    t,
    interactionMode,
    landingPageEnabled,
    tapProtectionEnabled,
    duplicateProtectionEnabled,
    duplicateWindowSeconds,
    dwellProtectionEnabled,
    dwellThresholdSeconds,
    testMode,
    setTestMode,
    setTapProtectionEnabled,
    setDuplicateProtectionEnabled,
    setDuplicateWindowSeconds,
    setDwellProtectionEnabled,
    setDwellThresholdSeconds,
}) => {
    const isDirectTap = !landingPageEnabled && interactionMode !== 'review_shield';
    const dwellApplies = !isDirectTap;

    return (
        <div className="border-t pt-4 space-y-4" data-testid="tap-protection-section">
            {/* OWNER TEST MODE — so an owner's own taps are never silently eaten
                by duplicate protection, and never counted as real traffic. */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-md border border-amber-300/70 bg-amber-50/60">
                <div className="flex-1">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                        <Shield className="w-4 h-4 text-amber-600" />
                        {t('tapProtection.testMode', 'Test mode')}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {t('tapProtection.testModeDesc',
                           'Your own test taps always register and are kept out of real statistics. Turn off when the card goes live.')}
                    </p>
                </div>
                <Switch
                    checked={!!testMode}
                    onCheckedChange={setTestMode}
                    data-testid="card-test-mode-toggle"
                />
            </div>

            {/* Master toggle */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                        <Shield className="w-4 h-4 text-cyan-500" />
                        {t('tapProtection.title', 'Tap Protection')}
                        <HelpButton docId="tapProtection" />
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {t(
                            'tapProtection.desc',
                            'Anti-fraud validation for incoming taps. When off, every tap is counted with no filtering.',
                        )}
                    </p>
                </div>
                <Switch
                    checked={!!tapProtectionEnabled}
                    onCheckedChange={setTapProtectionEnabled}
                    data-testid="tap-protection-master-toggle"
                />
            </div>

            <div className={tapProtectionEnabled ? '' : 'opacity-60'}>
                {/* Duplicate Protection */}
                <div
                    className="mt-3 p-3 rounded-md border bg-slate-50/40"
                    data-testid="duplicate-protection-subsection"
                >
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                            <Label className="text-sm font-medium">
                                {t('tapProtection.duplicate.title', 'Duplicate Protection')}
                            </Label>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                {t(
                                    'tapProtection.duplicate.desc',
                                    'Prevent repeated taps from the same device within the selected time window.',
                                )}
                            </p>
                        </div>
                        <Switch
                            checked={!!duplicateProtectionEnabled}
                            onCheckedChange={setDuplicateProtectionEnabled}
                            disabled={!tapProtectionEnabled}
                            data-testid="duplicate-protection-toggle"
                        />
                    </div>
                    {duplicateProtectionEnabled ? (
                        <div className="mt-3 flex items-center gap-2">
                            <Label className="text-xs text-slate-600">
                                {t('tapProtection.duplicate.window', 'Window (seconds)')}
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                max={600}
                                value={duplicateWindowSeconds ?? 30}
                                onChange={(e) =>
                                    setDuplicateWindowSeconds(
                                        Math.max(1, parseInt(e.target.value || '30', 10)),
                                    )
                                }
                                className="w-24"
                                disabled={!tapProtectionEnabled}
                                data-testid="duplicate-protection-window-input"
                            />
                        </div>
                    ) : null}
                </div>

                {/* Dwell Protection */}
                <div
                    className={`mt-3 p-3 rounded-md border ${
                        dwellApplies ? 'bg-slate-50/40' : 'bg-slate-100/80'
                    }`}
                    data-testid="dwell-protection-subsection"
                >
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                            <Label className="text-sm font-medium">
                                {t('tapProtection.dwell.title', 'Minimum Dwell Protection')}
                            </Label>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                {t(
                                    'tapProtection.dwell.desc',
                                    'Require the customer to remain on the Landing Page or Review Shield for a minimum time before the tap is counted.',
                                )}
                            </p>
                            {!dwellApplies ? (
                                <div
                                    className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                                    data-testid="dwell-protection-not-applicable-notice"
                                >
                                    {t(
                                        'tapProtection.dwell.notApplicable',
                                        'This protection only applies to Landing Page and Review Shield cards. Direct Tap cards do not render a page, so there is no dwell time to measure.',
                                    )}
                                </div>
                            ) : null}
                        </div>
                        <Switch
                            checked={!!dwellProtectionEnabled}
                            onCheckedChange={setDwellProtectionEnabled}
                            disabled={!tapProtectionEnabled || !dwellApplies}
                            data-testid="dwell-protection-toggle"
                        />
                    </div>
                    {dwellProtectionEnabled && dwellApplies ? (
                        <div className="mt-3 flex items-center gap-2">
                            <Label className="text-xs text-slate-600">
                                {t('tapProtection.dwell.threshold', 'Threshold (seconds)')}
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                max={120}
                                value={dwellThresholdSeconds ?? 10}
                                onChange={(e) =>
                                    setDwellThresholdSeconds(
                                        Math.max(1, parseInt(e.target.value || '10', 10)),
                                    )
                                }
                                className="w-24"
                                disabled={!tapProtectionEnabled}
                                data-testid="dwell-protection-threshold-input"
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};


/**
 * Per-card Lead Generation override (Gift Generator mode only).
 *
 * Deliberately the SAME Inherit/Override radio contract as the Enabled
 * Languages picker above it in this modal: `null` = inherit the org's Voucher
 * Settings, `{enabled, url}` = override for this card. `url: null` while
 * enabled means "override the toggle, keep the org's link", so a demo card can
 * be flipped on without restating the same App Store URL.
 */
const LeadGenerationOverrideSection = ({ t, value, onChange, orgDefault, setupName }) => {
    const inheriting = value === null || value === undefined;
    const enabled = inheriting ? !!orgDefault?.enabled : !!value?.enabled;
    const url = inheriting ? (orgDefault?.url || '') : (value?.url || '');
    const inheritedLabel = orgDefault?.enabled
        ? t('giftGenerator.leadGenOn', 'on')
        : t('giftGenerator.leadGenOff', 'off');

    return (
        <div
            className="mt-3 rounded-lg border border-fuchsia-200 dark:border-fuchsia-900/60 bg-white/70 dark:bg-slate-900/40 p-3 space-y-2"
            data-testid="card-lead-generation-section"
        >
            <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-fuchsia-600" />
                    {t('giftGenerator.leadGeneration', 'Lead Generation')}
                </Label>
                {!inheriting && (
                    <button
                        type="button"
                        onClick={() => onChange(null)}
                        className="text-xs text-fuchsia-600 dark:text-fuchsia-400 hover:underline"
                        data-testid="card-lead-generation-reset"
                    >
                        {t('languages.resetToOrg', 'Reset to organization default')}
                    </button>
                )}
            </div>

            <div className="flex gap-4 text-sm pl-1">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="radio"
                        checked={inheriting}
                        onChange={() => onChange(null)}
                        className="h-3.5 w-3.5 accent-fuchsia-600"
                        data-testid="card-lead-generation-inherit-radio"
                    />
                    <span>
                        {/* The inherit text names the SETUP this card runs, so
                            "on/off" is never read against the wrong package. */}
                        {setupName
                            ? t('giftSetups.inheritFromSetup', { name: setupName, state: inheritedLabel })
                            : <>{t('languages.inheritFromOrg', 'Inherit from organization')}
                                <span className="text-muted-foreground text-xs ml-1.5">({inheritedLabel})</span>
                              </>}
                    </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="radio"
                        checked={!inheriting}
                        onChange={() => onChange({ enabled: !!orgDefault?.enabled, url: null })}
                        className="h-3.5 w-3.5 accent-fuchsia-600"
                        data-testid="card-lead-generation-override-radio"
                    />
                    <span>{t('languages.overrideForThis', 'Override for this')}</span>
                </label>
            </div>

            <div className="flex items-center justify-between gap-3 pl-1">
                <span className={`text-xs ${inheriting ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
                    {t('giftGenerator.leadGenCardToggle', 'Offer double the prize for an app download')}
                </span>
                <Switch
                    checked={enabled}
                    disabled={inheriting}
                    onCheckedChange={(v) => onChange({ enabled: !!v, url: value?.url || null })}
                    data-testid="card-lead-generation-switch"
                />
            </div>

            {!inheriting && enabled && (
                <div className="space-y-1 pl-1">
                    <Label className="text-xs text-muted-foreground">
                        {t('giftGenerator.leadGenUrl', 'Download link')}
                    </Label>
                    <Input
                        value={url}
                        onChange={(e) => onChange({ enabled: true, url: e.target.value || null })}
                        placeholder={orgDefault?.url || 'https://apps.apple.com/...'}
                        data-testid="card-lead-generation-url-input"
                    />
                    <p className="text-[10px] text-muted-foreground/80" data-testid="card-lead-generation-url-hint">
                        {t('giftGenerator.leadGenUrlHint', 'Leave empty to use the organization link.')}
                    </p>
                </div>
            )}
        </div>
    );
};

/**
 * Loyal Review merchant configuration.
 *
 * Locked scope guardrails (see PRD "Loyal Review" section):
 *   - Positive Review Threshold is inherited from the existing
 *     Review Shield setting — NEVER duplicated here.
 *   - Reward dropdown reads from `/api/vouchers/loyal-review-available`
 *     which returns vouchers the merchant has already flagged via the
 *     existing Voucher creation flow (the "Available for Loyal Review"
 *     checkbox). No new voucher workflow ships here.
 *   - Toggle: Standard vs Loyal Review. Standard = existing behaviour
 *     byte-for-byte. Loyal Review requires a reward selection before
 *     the customer flow ever triggers.
 */
const LoyalReviewSection = ({
    t,
    token,
    organizationId,
    loyalReviewEnabled,
    loyalReviewVoucherId,
    loyalReviewAutoFlow,
    setLoyalReviewEnabled,
    setLoyalReviewVoucherId,
    setLoyalReviewAutoFlow,
    giftGeneratorEnabled,
    setGiftGeneratorEnabled,
    leadGenerationOverride,
    setLeadGenerationOverride,
    giftGeneratorSetupId,
    setGiftGeneratorSetupId,
    publicReviewThreshold,
}) => {
    const [rewards, setRewards] = useState([]);
    const [loadingRewards, setLoadingRewards] = useState(false);
    const [ggAvailable, setGgAvailable] = useState(null); // null=loading, true/false
    // The named Gift Generator setups this org has. The selected one decides
    // what the Lead Generation "Inherit" radio reports.
    const [giftSetups, setGiftSetups] = useState([]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
        fetch(`/api/voucher-settings/gift-setups${qs}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : { setups: [] }))
            .then((d) => { if (!cancelled) setGiftSetups(Array.isArray(d?.setups) ? d.setups : []); })
            .catch(() => { if (!cancelled) setGiftSetups([]); });
        return () => { cancelled = true; };
    }, [token, organizationId, giftGeneratorEnabled]);

    // Card setup → org default setup. This is what the card actually runs.
    const resolvedSetup = giftSetups.find((s) => s.id === giftGeneratorSetupId)
        || giftSetups.find((s) => s.is_default)
        || null;
    const orgDefaultSetupName = (giftSetups.find((s) => s.is_default) || {}).name || '';
    const setupLeadGen = {
        enabled: !!resolvedSetup?.lead_generation_enabled,
        url: resolvedSetup?.lead_generation_url || null,
    };

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoadingRewards(true);
        const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
        fetch(`/api/vouchers/loyal-review-available${qs}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : { items: [] }))
            .then((d) => { if (!cancelled) setRewards(Array.isArray(d?.items) ? d.items : []); })
            .catch(() => { if (!cancelled) setRewards([]); })
            .finally(() => { if (!cancelled) setLoadingRewards(false); });
        return () => { cancelled = true; };
    }, [token, loyalReviewEnabled, organizationId]);

    // Is Gift Generator turned on + has at least one gift at the org level?
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
        fetch(`/api/voucher-settings${qs}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : {}))
            .then((d) => {
                if (cancelled) return;
                const gg = d?.gift_generator || {};
                setGgAvailable(!!gg.enabled && Array.isArray(gg.gifts) && gg.gifts.length > 0);
            })
            .catch(() => { if (!cancelled) setGgAvailable(false); });
        return () => { cancelled = true; };
    }, [token, organizationId, giftGeneratorEnabled]);

    const empty = !loadingRewards && rewards.length === 0;
    const mode = giftGeneratorEnabled ? 'gift' : (loyalReviewEnabled ? 'loyal' : 'standard');
    const selectMode = (id) => {
        // Mutually exclusive primary positive experience.
        setLoyalReviewEnabled(id === 'loyal');
        setGiftGeneratorEnabled(id === 'gift');
    };
    return (
        <div
            className="border rounded-lg p-3 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 dark:from-emerald-950/20 dark:to-teal-950/20"
            data-testid="loyal-review-section"
        >
            <Label className="text-sm font-semibold flex items-center gap-2 mb-1">
                <Gift className="w-4 h-4 text-emerald-500" />
                {t('loyalReview.merchant.positiveHeading', 'Positive Experience')}
                <HelpButton docId="loyalReview" />
            </Label>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                {t(
                    'loyalReview.merchant.positiveHint',
                    'What guests who rate {{n}}+ stars see next. Standard sends them straight to the public review flow. Loyal Review offers them a choice between a gift and a public review.',
                    { n: publicReviewThreshold },
                )}
            </p>
            <div
                className="grid grid-cols-3 gap-2 mb-3"
                role="radiogroup"
                aria-label="Positive-review behavior"
            >
                {[
                    { id: 'standard', label: t('loyalReview.merchant.standard', 'Standard'), disabled: false },
                    { id: 'loyal',    label: t('loyalReview.merchant.loyal', 'Loyal Review'), disabled: false },
                    { id: 'gift',     label: t('giftGenerator.cardLabel', 'Gift Generator'), disabled: ggAvailable === false && mode !== 'gift' },
                ].map((opt) => {
                    const active = mode === opt.id;
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            disabled={opt.disabled}
                            onClick={() => selectMode(opt.id)}
                            data-testid={`loyal-review-mode-${opt.id}-btn`}
                            className={
                                'py-2.5 rounded-md border-2 text-xs font-semibold transition-all ' +
                                (active
                                    ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
                                    : opt.disabled
                                        ? 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-emerald-300')
                            }
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
            {mode === 'gift' && (
                <p className="text-xs text-fuchsia-700 dark:text-fuchsia-300 leading-relaxed" data-testid="gift-generator-card-hint">
                    {t('giftGenerator.cardHint', 'Let guests tap this card to receive a surprise gift.')}
                </p>
            )}
            {mode === 'gift' && giftSetups.length > 0 && (
                <div className="mt-3 rounded-lg border border-fuchsia-200 dark:border-fuchsia-900/60 bg-white/70 dark:bg-slate-900/40 p-3 space-y-2"
                     data-testid="card-gift-setup-section">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                        <Layers className="w-4 h-4 text-fuchsia-600" />
                        {t('giftSetups.cardSectionTitle', 'Gift Generator setup')}
                    </Label>
                    <Select
                        value={giftGeneratorSetupId || '__default__'}
                        onValueChange={(v) => setGiftGeneratorSetupId(v === '__default__' ? null : v)}
                    >
                        <SelectTrigger className="h-9" data-testid="card-gift-setup-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__default__" data-testid="card-gift-setup-option-default">
                                {orgDefaultSetupName
                                    ? t('giftSetups.cardUseDefaultNamed', { name: orgDefaultSetupName })
                                    : t('giftSetups.cardUseDefault', 'Organization default')}
                            </SelectItem>
                            {giftSetups.filter((s) => !s.is_default).map((s) => (
                                <SelectItem key={s.id} value={s.id} data-testid={`card-gift-setup-option-${s.id}`}>
                                    {s.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                        {t('giftSetups.cardSectionHint')}
                    </p>
                </div>
            )}
            {mode === 'gift' && (
                <LeadGenerationOverrideSection
                    t={t}
                    value={leadGenerationOverride ?? null}
                    onChange={setLeadGenerationOverride}
                    orgDefault={setupLeadGen}
                    setupName={resolvedSetup?.name}
                />
            )}
            {ggAvailable === false && mode !== 'gift' && (
                <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed" data-testid="gift-generator-unavailable-hint">
                    {t('giftGenerator.cardNeedsOrg', 'To use Gift Generator, first turn it on and add gifts in Voucher Settings → Gift Generator.')}
                </p>
            )}
            {loyalReviewEnabled && (
                <div className="space-y-1 mt-2" data-testid="loyal-review-reward-picker">
                    <Label className="text-xs text-muted-foreground">
                        {t('loyalReview.merchant.rewardLabel', 'Reward')}
                    </Label>
                    {empty ? (
                        <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed"
                           data-testid="loyal-review-reward-empty">
                            {t(
                                'loyalReview.merchant.rewardEmpty',
                                'No vouchers are marked "Available for Loyal Review" yet. Create one from the Vouchers page.',
                            )}
                        </p>
                    ) : (
                        <Select
                            value={loyalReviewVoucherId || ''}
                            onValueChange={(v) => setLoyalReviewVoucherId(v)}
                        >
                            <SelectTrigger data-testid="loyal-review-reward-select">
                                <SelectValue
                                    placeholder={t(
                                        'loyalReview.merchant.rewardPlaceholder',
                                        'Select existing voucher',
                                    )}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {rewards.map((v) => (
                                    <SelectItem key={v.id} value={v.id}>
                                        {`$${Number(v.value || 0).toFixed(0)}`}
                                        {v.location_name ? ` · ${v.location_name}` : ''}
                                        {` · ${v.code}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    {/* Auto Flow — gift screen registers the voucher and sends
                        the guest to the review page on its own. Off = today's
                        manual screen with Save + Continue. */}
                    <div className="flex items-start justify-between gap-3 pt-3 mt-1 border-t border-emerald-200/60 dark:border-emerald-900/40">
                        <div className="min-w-0">
                            <Label htmlFor="loyal-review-auto-flow-switch" className="text-xs font-semibold">
                                {t('loyalReview.merchant.autoFlowLabel', 'Auto Flow')}
                            </Label>
                            <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                                {t('loyalReview.merchant.autoFlowHint', 'Skip the save step: the gift is emailed automatically and the guest is taken straight to the review page.')}
                            </p>
                        </div>
                        <Switch
                            id="loyal-review-auto-flow-switch"
                            checked={!!loyalReviewAutoFlow}
                            onCheckedChange={(v) => setLoyalReviewAutoFlow(!!v)}
                            data-testid="loyal-review-auto-flow-switch"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};



export const CardSettingsModal = ({
    open, onOpenChange, selectedCard,
    state, handlers, refs,
    isSuperAdmin, t, token, canUseAds, hasPermission,
    isDirty = false,
    isSaving = false,
    changesDiff = [],
    isAutomotiveOrg = false,
    isMedicalOrg = false,
}) => {
    const {
        interactionMode, landingPageEnabled,
        landingShowText, landingHeadline, landingDescription,
        landingLogoUrl, landingLogoSize, landingAutoRedirect, landingRedirectDelay,
        countdownClockEnabled, countdownClockColor,
        showYulontapBranding, multipleAdsEnabled, adImages, sameTextForAll, adTexts,
        scheduleEnabled, scheduleTimezone, scheduleEntries,
        uploadingLogo,
        reviewShieldLogoUrl, reviewShieldLogoSize, reviewShieldBackgroundImageUrl, languageMode,
        cardStyle = '',
        enabledLanguages,
        leadGenerationOverride = null,
        giftGeneratorSetupId = null,
        setGiftGeneratorSetupId = () => {},
        reviewShieldQuestions,
        publicReviewThreshold = 4,
        privateReviewEnabled = false,
        experienceType = 'dine_in',
        savingExperienceType = false,
        guestExperienceVersion = 'v1',
        timingEnabled = false,
        timingSchedule = [],
        notificationEmails,
        notificationPhones,
        directResponseEnabled,
        requireEmployeePin,
        reviewCounterEnabled,
        tapProtectionEnabled = null,
        duplicateProtectionEnabled = null,
        duplicateWindowSeconds = null,
        dwellProtectionEnabled = null,
        dwellThresholdSeconds = null,
        testMode = false,
        giftCardEnabled,
        loyalReviewEnabled = false,
        loyalReviewVoucherId = '',
        loyalReviewAutoFlow = false,
        giftGeneratorEnabled = false,
        globalReviewEnabled, useReviewShieldWithGlobalReview, globalReviewDestinations,
        globalReviewLogoUrl, globalReviewLogoSize, globalReviewBackgroundImageUrl, globalReviewMessageEn, globalReviewMessageFr,
        hotelOpsLogoUrl = '', hotelOpsLogoSize = 'medium', hotelOpsBackgroundImageUrl = '',
        guestServicesContext = 'standard', poolDrinksEnabled = true, poolFoodEnabled = true,
        poolTowelsEnabled = true, poolOtherEnabled = true, poolDrinkMenuImageUrl = '', poolFoodMenuImageUrl = '',

        landingVideoEnabled, mediaType, mediaVideoUrl, mediaVideoId, mediaVideoPosterUrl,
        mediaVideoFallbackImage, mediaAutoplay, mediaLoop, mediaMuted,
        mediaSkipEnabled, mediaSkipDelay, uploadingVideo, videoUploadProgress
    } = state;

    const {
        setInteractionMode, setLandingPageEnabled,
        setLandingShowText, setLandingHeadline, setLandingDescription,
        setLandingLogoUrl, setLandingLogoSize, setLandingAutoRedirect, setLandingRedirectDelay,
        setCountdownClockEnabled, setCountdownClockColor,
        setShowYulontapBranding, setMultipleAdsEnabled, setAdImages: setAdImagesRaw, setSameTextForAll, setAdTexts,
        setScheduleEnabled, setScheduleTimezone, setScheduleEntries,
        setPendingMode, setShowModeSwitchConfirm, setShowBulkApplyModal,
        handleSaveLandingSettings, handleLogoUpload, handleAdImageUpload, clearLogo,
        setReviewShieldLogoUrl, setReviewShieldLogoSize, setReviewShieldBackgroundImageUrl, setLanguageMode,
        setCardStyle = () => {},
        setEnabledLanguages,
        setLeadGenerationOverride = () => {},
        clearReviewShieldLogo, handleReviewShieldLogoUpload,
        handleReviewShieldWallpaperUpload, handleGlobalReviewWallpaperUpload,
        setReviewShieldQuestions,
        setPublicReviewThreshold,
        setPrivateReviewEnabled = () => {},
        setExperienceType,
        onExperienceTypeChange = () => {},
        setGuestExperienceVersion = () => {},
        setTimingEnabled,
        setTimingSchedule,
        setNotificationEmails,
        setNotificationPhones,
        setDirectResponseEnabled,
        setRequireEmployeePin,
        setReviewCounterEnabled,
        setTapProtectionEnabled = () => {},
        setDuplicateProtectionEnabled = () => {},
        setDuplicateWindowSeconds = () => {},
        setDwellProtectionEnabled = () => {},
        setDwellThresholdSeconds = () => {},
        setTestMode = () => {},
        setGiftCardEnabled,
        setLoyalReviewEnabled = () => {},
        setLoyalReviewVoucherId = () => {},
        setLoyalReviewAutoFlow = () => {},
        setGiftGeneratorEnabled = () => {},
        setGlobalReviewEnabled, setUseReviewShieldWithGlobalReview, setGlobalReviewDestinations,
        setGlobalReviewLogoUrl, setGlobalReviewLogoSize, setGlobalReviewBackgroundImageUrl, setGlobalReviewMessageEn, setGlobalReviewMessageFr,

        handleGlobalReviewLogoUpload, globalReviewLogoInputRef,
        setHotelOpsLogoUrl = () => {}, setHotelOpsLogoSize = () => {}, setHotelOpsBackgroundImageUrl = () => {},
        clearHotelOpsLogo = () => {}, handleHotelOpsLogoUpload = () => {}, handleHotelOpsWallpaperUpload = () => {},
        setGuestServicesContext = () => {}, setPoolDrinksEnabled = () => {}, setPoolFoodEnabled = () => {},
        setPoolTowelsEnabled = () => {}, setPoolOtherEnabled = () => {}, setPoolDrinkMenuImageUrl = () => {}, setPoolFoodMenuImageUrl = () => {},
        handlePoolDrinkMenuUpload = () => {}, handlePoolFoodMenuUpload = () => {},
        setLandingVideoEnabled, setMediaType, setMediaVideoUrl, setMediaVideoId,
        setMediaVideoPosterUrl, setMediaVideoFallbackImage,
        setMediaAutoplay, setMediaLoop, setMediaMuted, setMediaSkipEnabled, setMediaSkipDelay,
        handleVideoUpload, removeVideo
    } = handlers;

    const { logoInputRef, adImageInputRef, reviewShieldLogoInputRef, videoInputRef, reviewShieldWallpaperInputRef, globalReviewWallpaperInputRef, hotelOpsLogoInputRef, hotelOpsWallpaperInputRef, poolDrinkMenuInputRef, poolFoodMenuInputRef } = refs;


    // Permission check — Super Admin sees everything; others need explicit permission
    const _p = (key) => hasPermission ? hasPermission(key) : true;

    // Org brand (wallpaper + default surface) for the live card-style preview.
    const orgIdForBrand = selectedCard?.organization_id;
    const [orgBrand, setOrgBrand] = useState(null);
    useEffect(() => {
        if (!open || !orgIdForBrand || !token) { setOrgBrand(null); return; }
        let cancelled = false;
        fetch(`/api/voucher-settings?organization_id=${encodeURIComponent(orgIdForBrand)}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (!cancelled) setOrgBrand(d || {}); })
            .catch(() => { if (!cancelled) setOrgBrand({}); });
        return () => { cancelled = true; };
    }, [open, orgIdForBrand, token]);

    // Photo attachments on the Review Shield private path — ORGANIZATION-WIDE.
    // Unset = today's behaviour (on); a clinic starts off, so no attached image
    // can carry medical information.
    const [photosEnabled, setPhotosEnabled] = useState(null);
    const [savingPhotos, setSavingPhotos] = useState(false);
    useEffect(() => {
        if (!open || !orgIdForBrand || !token) { setPhotosEnabled(null); return; }
        let cancelled = false;
        fetch(`/api/review-shield/photo-setting?organization_id=${encodeURIComponent(orgIdForBrand)}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (!cancelled && d) setPhotosEnabled(d.photos_enabled !== false); })
            .catch(() => { /* leave unknown; the toggle simply does not render */ });
        return () => { cancelled = true; };
    }, [open, orgIdForBrand, token]);

    const savePhotosEnabled = async (next) => {
        setSavingPhotos(true);
        try {
            const r = await fetch(`/api/review-shield/photo-setting?organization_id=${encodeURIComponent(orgIdForBrand)}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ photos_enabled: next }),
            });
            if (!r.ok) throw new Error('save failed');
            setPhotosEnabled(next);
            toast.success(t('common.saved', 'Saved'));
        } catch (_) {
            toast.error(t('common.error', 'Something went wrong'));
        } finally {
            setSavingPhotos(false);
        }
    };

    // ── Promo mode: the pinned promo lives here so the modal's own
    // "Save Changes" can persist it too, through the SAME narrow promo
    // endpoint the panel's Apply button uses. The landing-settings endpoint
    // and its ~60 fields are untouched.
    const [promoId, setPromoId] = useState('');
    const [promoIdSaved, setPromoIdSaved] = useState('');
    useEffect(() => {
        const pinned = selectedCard?.promo_id || '';
        setPromoId(pinned);
        setPromoIdSaved(pinned);
    }, [open, selectedCard?.id, selectedCard?.promo_id]);
    const [promoSecondDirty, setPromoSecondDirty] = useState(false);
    const promoDirty = interactionMode === 'promo'
        && (promoId !== promoIdSaved || promoSecondDirty);

    const promoSecondRef = useRef({});
    const savePromoPin = async () => {
        if (!selectedCard?.card_id) return true;
        const sb = promoSecondRef.current || {};
        try {
            await api.put(`/promo-card-mode/${selectedCard.card_id}`, {
                promo_id: promoId || null,
                logo_url: reviewShieldLogoUrl || '',
                background_image_url: reviewShieldBackgroundImageUrl || '',
                enabled: true,
                second_button_enabled: !!sb.enabled,
                second_button_text: sb.text || { en: '', fr: '', es: '' },
                second_button_color: sb.color || '',
                second_button_position: sb.position || 'below',
                second_button_url: sb.url || '',
            });
            setPromoIdSaved(promoId);
            promoSecondRef.current?.markSaved?.();
            setPromoSecondDirty(false);
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || t('cardSettings.promoApplyFailed', 'Could not apply promo mode'));
            return false;
        }
    };

    // Save Changes. In Promo mode the promo pin is written AFTER the normal
    // save, so the pin can never be lost by the normal save.
    const handleSaveAll = async () => {
        const ok = await handleSaveLandingSettings();
        if (interactionMode === 'promo' && ok !== false) await savePromoPin();
        return ok;
    };

    // Derived "effective mode" — what the dropdown displays. The tap routing
    // backend still reads each underlying flag, but operators think in
    // single-choice terms ("this card is in X mode"). Computed from the four
    // raw flags so legacy cards in any state map cleanly to one of the four.
    const effectiveMode = useMemo(() => {
        if (interactionMode === 'hotel') return 'hotel';
        if (interactionMode === 'guest_assistant') return 'guest_assistant';
        if (interactionMode === 'hotel_staff') return 'hotel_staff';
        if (interactionMode === 'automotive_used_vehicle') return 'automotive_used_vehicle';
        // Gift Generator Game is a first-class card experience. It takes
        // precedence over Review Shield to mirror the /api/tap decision tree
        // (gift_generator_enabled is checked before review_shield).
        if (giftGeneratorEnabled) return 'gift_generator';
        if (interactionMode === 'promo') return 'promo';
        if (interactionMode === 'review_shield') return 'review_shield';
        if (interactionMode === 'advertising' || landingPageEnabled) return 'advertising';
        if (globalReviewEnabled) return 'global_review';
        return 'standard';
    }, [interactionMode, landingPageEnabled, globalReviewEnabled, giftGeneratorEnabled]);

    const handleModeChange = (newMode) => {
        // v4.34.4 — Single source of truth for card destination behavior.
        // Effective dropdown values are: standard / advertising / review_shield / global_review.
        // We translate them into the underlying flag set so the existing tap
        // routing logic continues to work unchanged.
        //
        // Underlying flags written per effective mode:
        //   standard       → interaction_mode=standard,        landing=false, global_review=false
        //   advertising    → interaction_mode=advertising,     landing=true,  global_review=false
        //   review_shield  → interaction_mode=review_shield,   landing=false, global_review preserved (combo lives inside RS panel)
        //   global_review  → interaction_mode=standard,        landing=false, global_review=true
        //
        // Mutually-exclusive transitions that destroy meaningful config still
        // route through ModeSwitchDialog (Review Shield ↔ Advertising).
        if (newMode === 'review_shield' && (landingPageEnabled || effectiveMode === 'advertising')) {
            setPendingMode(newMode);
            setShowModeSwitchConfirm(true);
            return;
        }
        if (newMode === 'advertising' && interactionMode === 'review_shield') {
            setPendingMode(newMode);
            setShowModeSwitchConfirm(true);
            return;
        }

        // Gift Generator Game is a first-class, mutually-exclusive card
        // experience: exactly one mode owns gift_generator_enabled. Any mode
        // change re-derives the flag so switching to another experience turns
        // the game OFF, and switching to it turns the game ON.
        setGiftGeneratorEnabled(newMode === 'gift_generator');

        switch (newMode) {
            case 'standard':
                setInteractionMode('standard');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'advertising':
                setInteractionMode('advertising');
                setLandingPageEnabled(true);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'review_shield':
                setInteractionMode('review_shield');
                setLandingPageEnabled(false);
                // global_review_enabled left as-is; surfaces via the combo toggle inside the Review Shield panel.
                break;
            case 'global_review':
                setInteractionMode('standard');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(true);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'hotel':
                setInteractionMode('hotel');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'guest_assistant':
                setInteractionMode('guest_assistant');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'hotel_staff':
                setInteractionMode('hotel_staff');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'automotive_used_vehicle':
                // Dedicated Automotive experience — clear the overlay toggles
                // like the other exclusive modes. Routing to /vehicle/{card}
                // is driven purely by this interaction_mode.
                setInteractionMode('automotive_used_vehicle');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'promo':
                // The card shows only its promo screen. Exclusive, like the
                // other first-class experiences; nothing else is written.
                setInteractionMode('promo');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                break;
            case 'gift_generator':
                // First-class Gift Generator Game card. gift_generator_enabled
                // (set above) drives /api/tap → /demo/{card}. Clear the other
                // exclusive experiences; the game config lives at org level.
                setInteractionMode('standard');
                setLandingPageEnabled(false);
                setGlobalReviewEnabled(false);
                setUseReviewShieldWithGlobalReview(false);
                setLoyalReviewEnabled(false);
                break;
            default:
                setInteractionMode(newMode);
        }
    };

    // Notification email management
    const [emailInput, setEmailInput] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneNameInput, setPhoneNameInput] = useState('');
    const [showQuestionManager, setShowQuestionManager] = useState(false);

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const addNotificationEmail = () => {
        const email = emailInput.trim().toLowerCase();
        if (!email) return;
        const emails = notificationEmails || [];
        if (!isValidEmail(email)) {
            toast.error(t('reviewShield.invalidEmail', 'Please enter a valid email address'));
            return;
        }
        if (emails.includes(email)) {
            toast.error(t('reviewShield.duplicateEmail', 'This email is already added'));
            return;
        }
        if (emails.length >= 10) {
            toast.error(t('reviewShield.maxRecipientsReached', 'Maximum 10 recipients allowed'));
            return;
        }
        setNotificationEmails([...emails, email]);
        setEmailInput('');
    };

    const removeNotificationEmail = (email) => {
        setNotificationEmails((notificationEmails || []).filter(e => e !== email));
    };

    const recipientPhone = (p) => (typeof p === 'object' && p !== null) ? (p.phone || p.phone_number || '') : p;
    const recipientName = (p) => (typeof p === 'object' && p !== null) ? (p.name || '') : '';

    const addNotificationPhone = () => {
        const raw = phoneInput.trim();
        if (!raw) return;
        const phones = notificationPhones || [];
        // v3.5.13 — Accept friendly formats ("(514) 555-1234", "514-555-1234",
        // "5145551234"). Normalize to E.164 so Twilio accepts it and dedupe.
        const normalized = normalizePhoneE164(raw);
        if (!normalized) {
            toast.error(t('reviewShield.invalidPhone', 'Enter a valid phone number (10+ digits)'));
            return;
        }
        if (phones.some(p => recipientPhone(p) === normalized)) {
            toast.error(t('reviewShield.duplicatePhone', 'This phone number is already added'));
            return;
        }
        if (phones.length >= 10) {
            toast.error(t('reviewShield.maxPhoneRecipients', 'Maximum 10 phone recipients allowed'));
            return;
        }
        setNotificationPhones([...phones, { phone: normalized, name: phoneNameInput.trim() }]);
        setPhoneInput('');
        setPhoneNameInput('');
    };


    const removeNotificationPhone = (phone) => {
        setNotificationPhones((notificationPhones || []).filter(p => recipientPhone(p) !== phone));
    };

    const addScheduleEntry = () => {
        setScheduleEntries(prev => [...(prev || []), {
            name: `Schedule ${(prev || []).length + 1}`,
            days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            start_time: '09:00',
            end_time: '17:00'
        }]);
    };

    const updateScheduleEntry = (index, field, value) => {
        setScheduleEntries(prev => {
            const updated = [...(prev || [])];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const removeScheduleEntry = (index) => {
        setScheduleEntries(prev => (prev || []).filter((_, i) => i !== index));
    };

    const toggleScheduleDay = (entryIndex, day) => {
        setScheduleEntries(prev => {
            const updated = [...(prev || [])];
            const entry = { ...updated[entryIndex] };
            const days = entry.days || [];
            entry.days = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
            updated[entryIndex] = entry;
            return updated;
        });
    };

    const removeAdImage = (index) => {
        setAdImagesRaw(prev => (prev || []).filter((_, i) => i !== index));
        setAdTexts(prev => (prev || []).filter((_, i) => i !== index));
    };

    const updateAdText = (index, field, value) => {
        setAdTexts(prev => {
            const updated = [...(prev || [])];
            if (!updated[index]) updated[index] = {};
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('cardSettings.title', { cardId: selectedCard?.card_id })}
                    </DialogTitle>
                    <DialogDescription>
                        {t('cardSettings.description')}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-2">
                    {/* Interaction Mode Selector */}
                    <div className="space-y-2">
                        <Label className="text-base font-medium">{t('reviewShield.interactionMode', 'Interaction Mode')}</Label>
                        <Select value={effectiveMode} onValueChange={handleModeChange}>
                            <SelectTrigger data-testid="interaction-mode-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="standard">
                                    <span className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                        {t('reviewShield.modeStandard', 'Standard Review')}
                                    </span>
                                </SelectItem>
                                {(_p('card_landing_page') || _p('card_adverts')) && (
                                    <SelectItem value="advertising">
                                        <span className="flex items-center gap-2">
                                            <Megaphone className="w-4 h-4 text-amber-500 shrink-0" />
                                            {t('reviewShield.modeAdvertising', 'Landing Page (Advertising)')}
                                        </span>
                                    </SelectItem>
                                )}
                                <SelectItem value="review_shield">
                                    <span className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-sky-500 shrink-0" />
                                        {t('reviewShield.modeReviewShield', 'Review Shield')}
                                    </span>
                                </SelectItem>
                                <SelectItem value="promo" data-testid="interaction-mode-opt-promo">
                                    <span className="flex items-center gap-2">
                                        <Megaphone className="w-4 h-4 text-fuchsia-500 shrink-0" />
                                        {t('cardSettings.modePromo', 'Promo')}
                                    </span>
                                </SelectItem>
                                <SelectItem value="loyalty" data-testid="interaction-mode-opt-loyalty">
                                    <span className="flex items-center gap-2">
                                        <BadgeCheck className="w-4 h-4 text-violet-500 shrink-0" />
                                        {t('cardSettings.modeLoyalty', 'Loyalty portal')}
                                    </span>
                                </SelectItem>
                                <SelectItem value="loyalty_desk" data-testid="interaction-mode-opt-loyalty-desk">
                                    <span className="flex items-center gap-2">
                                        <BadgeCheck className="w-4 h-4 text-amber-500 shrink-0" />
                                        {t('cardSettings.modeLoyaltyDesk', 'Org Loyalty Portal')}
                                    </span>
                                </SelectItem>
                                <SelectItem value="gift_generator" data-testid="interaction-mode-opt-gift-generator">
                                    <span className="flex items-center gap-2">
                                        <Gift className="w-4 h-4 text-fuchsia-500 shrink-0" />
                                        {t('reviewShield.modeGiftGenerator', 'Gift Generator Game 🎁')}
                                    </span>
                                </SelectItem>
                                {_p('card_global_review') && (
                                    <SelectItem value="global_review">
                                        <span className="flex items-center gap-2">
                                            <Globe className="w-4 h-4 text-fuchsia-500 shrink-0" />
                                            {t('reviewShield.modeGlobalReview', 'Global Review')}
                                        </span>
                                    </SelectItem>
                                )}
                                <SelectItem value="hotel" data-testid="interaction-mode-opt-hotel">
                                    <span className="flex items-center gap-2">
                                        <Hotel className="w-4 h-4 text-teal-500 shrink-0" />
                                        {t('reviewShield.modeHotel', 'Hotel')}
                                    </span>
                                </SelectItem>
                                <SelectItem value="guest_assistant" data-testid="interaction-mode-opt-guest-assistant">
                                    <span className="flex items-center gap-2">
                                        <Hotel className="w-4 h-4 text-emerald-500 shrink-0" />
                                        {t('reviewShield.modeGuestAssistant', 'Hotel — Guest Assistance Platform')}
                                    </span>
                                </SelectItem>
                                <SelectItem value="hotel_staff" data-testid="interaction-mode-opt-hotel-staff">
                                    <span className="flex items-center gap-2">
                                        <Hotel className="w-4 h-4 text-rose-500 shrink-0" />
                                        {t('reviewShield.modeHotelStaff', 'Hotel — Ops')}
                                    </span>
                                </SelectItem>
                                {isAutomotiveOrg && (
                                    <SelectItem value="automotive_used_vehicle" data-testid="interaction-mode-opt-automotive-used-vehicle">
                                        <span className="flex items-center gap-2">
                                            <Car className="w-4 h-4 text-indigo-500 shrink-0" />
                                            {t('reviewShield.modeAutomotiveUsedVehicle', 'Automotive — Used Vehicle Sales')}
                                        </span>
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        {interactionMode === 'guest_assistant' && (
                            <div className="mt-2 border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-2.5 text-xs space-y-0.5"
                                data-testid="gap-mode-context">
                                <p className="font-semibold">{t('reviewShield.gapCtxTitle', 'Guest Assistance Platform')}</p>
                                {selectedCard?.organization_name && (
                                    <p>{t('reviewShield.gapCtxHotel', 'Hotel:')} <span className="font-medium">{selectedCard.organization_name}</span></p>
                                )}
                                {selectedCard?.room_number && (
                                    <p>{t('reviewShield.gapCtxRoom', 'Room:')} <span className="font-medium">{selectedCard.room_number}</span></p>
                                )}
                                <p className="text-muted-foreground">
                                    {t('reviewShield.gapCtxNote', "Tapping this card opens this hotel's shared Guest Assistance Platform configuration (Hotels → Guest Assistance Platform).")}
                                </p>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            {effectiveMode === 'standard' && t('cardSettings.modeStandardDesc')}
                            {effectiveMode === 'advertising' && t('cardSettings.modeAdvertisingDesc')}
                            {effectiveMode === 'review_shield' && t('cardSettings.modeReviewShieldDesc')}
                            {effectiveMode === 'promo' && t('cardSettings.modePromoDesc',
                                'A tap shows only this card\'s promo screen, with its own logo and wallpaper. If no promo qualifies, the card falls back to the standard review flow.')}
                            {effectiveMode === 'global_review' && t(
                                'cardSettings.modeGlobalReviewDesc',
                                'Guests choose where to leave their feedback: Google, Facebook, Instagram, Trustpilot, Yelp, TripAdvisor.'
                            )}
                            {effectiveMode === 'hotel' && t(
                                'cardSettings.modeHotelDesc',
                                'Guests get the dedicated Hotel experience (rating, Wi-Fi, gift). Configure details in Hotel Setup.'
                            )}
                            {effectiveMode === 'automotive_used_vehicle' && t(
                                'cardSettings.modeAutomotiveUsedVehicleDesc',
                                'This card launches the Automotive Used Vehicle showroom at /vehicle/{cardId}, resolving to whichever vehicle is currently assigned to it.'
                            )}
                            {effectiveMode === 'gift_generator' && t(
                                'cardSettings.modeGiftGeneratorDesc',
                                "This card launches the Gift Generator game directly when tapped (/demo/{cardId}). It uses this organization's configured Gift Generator experience (Voucher Settings) — scratch/button, gifts, exchanges and design."
                            )}
                        </p>
                    </div>

                    {/* === Hotel Ops Branding — CONTEXTUAL: only for Interaction Mode "Hotel — Ops" === */}
                    {interactionMode === 'hotel_staff' && (
                        <div className="border border-rose-500/30 bg-rose-500/5 rounded-lg p-4 space-y-4" data-testid="hotel-ops-branding-block">
                            <div>
                                <p className="text-sm font-semibold flex items-center gap-2">
                                    <Hotel className="w-4 h-4 text-rose-500" />
                                    {t('cardSettings.hotelOpsBrandingTitle', 'Hotel Ops Branding')}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {t('cardSettings.hotelOpsBrandingHint', 'Logo and background shown on this card\u2019s staff-facing Hotel Ops screens. The background is an optional override — leave it empty to use the hotel\u2019s shared wallpaper.')}
                                </p>
                            </div>

                            {/* Hotel Ops Logo */}
                            <div>
                                <Label className="mb-1 block text-sm">
                                    <div className="flex items-center gap-2"><Image className="w-4 h-4" />{t('cardSettings.hotelOpsLogo', 'Hotel Ops Logo')} <span className="text-muted-foreground font-normal">(optional)</span></div>
                                </Label>
                                {hotelOpsLogoUrl && (
                                    <div className="mb-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border relative">
                                        <img src={hotelOpsLogoUrl} alt="Logo preview" className="max-h-16 max-w-[150px] object-contain mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                                        <button type="button" onClick={clearHotelOpsLogo} className="absolute top-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-600" title="Remove logo" data-testid="hotel-ops-clear-logo">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                                <div className="flex gap-2 mb-2">
                                    <input ref={hotelOpsLogoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml" onChange={handleHotelOpsLogoUpload} className="hidden" data-testid="hotel-ops-logo-file-input" />
                                    <Button type="button" variant="outline" size="sm" onClick={() => hotelOpsLogoInputRef.current?.click()} disabled={uploadingLogo} className="flex-1" data-testid="upload-hotel-ops-logo-btn">
                                        {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.uploading', 'Uploading...')}</>) : (<><Upload className="w-4 h-4 mr-2" />{t('cardSettings.uploadImage', 'Upload Image')}</>)}
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2 my-2">
                                    <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                    <span className="text-xs text-muted-foreground">{t('cardSettings.or', 'or')}</span>
                                    <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                </div>
                                <Input value={hotelOpsLogoUrl || ''} onChange={(e) => setHotelOpsLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" data-testid="hotel-ops-logo-url-input" />
                                {hotelOpsLogoUrl && (
                                    <div className="mt-3">
                                        <Label className="mb-2 block text-sm">{t('reviewShield.logoSize', 'Logo Size')}</Label>
                                        <div className="flex gap-2">
                                            {[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }, { value: 'very_large', label: 'Extra Large' }].map((size) => (
                                                <Button key={size.value} type="button" variant={hotelOpsLogoSize === size.value ? 'default' : 'outline'} size="sm" onClick={() => setHotelOpsLogoSize(size.value)} className="flex-1" data-testid={`hotel-ops-logo-size-${size.value}`}>{size.label}</Button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Hotel Ops Wallpaper / Background */}
                            <div className="space-y-2 border-t border-rose-500/20 pt-4" data-testid="hotel-ops-wallpaper-block">
                                <Label className="text-sm font-medium flex items-center gap-2">
                                    <Image className="w-4 h-4" />
                                    {t('cardSettings.hotelOpsWallpaper', 'Background image (wallpaper)')}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    {t('cardSettings.hotelOpsWallpaperHelp', 'Optional. Applied across the staff Hotel Ops screens. Leave empty to inherit the hotel\u2019s shared wallpaper.')}
                                </p>
                                <div className="flex gap-2">
                                    <input ref={hotelOpsWallpaperInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleHotelOpsWallpaperUpload} className="hidden" data-testid="hotel-ops-wallpaper-file-input" />
                                    <Button type="button" variant="outline" size="sm" onClick={() => hotelOpsWallpaperInputRef?.current?.click()} disabled={uploadingLogo} className="flex-1" data-testid="upload-hotel-ops-wallpaper-btn">
                                        {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.uploading', 'Uploading...')}</>) : (<><Upload className="w-4 h-4 mr-2" />{t('reviewShield.uploadWallpaperBtn', 'Upload wallpaper')}</>)}
                                    </Button>
                                    {hotelOpsBackgroundImageUrl && (
                                        <Button type="button" variant="outline" size="sm" onClick={() => setHotelOpsBackgroundImageUrl('')} data-testid="hotel-ops-wallpaper-clear-btn">
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                                <Input value={hotelOpsBackgroundImageUrl || ''} onChange={(e) => setHotelOpsBackgroundImageUrl(e.target.value)} placeholder={t('reviewShield.wallpaperOrPaste', 'or paste a hosted image URL (https://\u2026)')} data-testid="hotel-ops-wallpaper-url-input" />
                                {hotelOpsBackgroundImageUrl && (
                                    <div className="relative mt-2 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                                        <img src={hotelOpsBackgroundImageUrl} alt="" className="block w-full h-32 object-cover" onError={(e) => { e.target.style.opacity = '0.3'; }} data-testid="hotel-ops-wallpaper-preview" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* === Guest Services Entry Context — CONTEXTUAL: only for "Hotel — Guest Assistance Platform" === */}
                    {interactionMode === 'guest_assistant' && (
                        <div className="border border-teal-500/30 bg-teal-500/5 rounded-lg p-4 space-y-4" data-testid="guest-services-entry-block">
                            <div>
                                <p className="text-sm font-semibold flex items-center gap-2">
                                    <Image className="w-4 h-4 text-teal-500" />
                                    {t('cardSettings.entryContextTitle', 'Guest Services Entry')}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {t('cardSettings.entryContextHint', 'Choose what this card opens to after the guest signs in. Standard shows the normal Guest Services menu.')}
                                </p>
                            </div>
                            <Select value={guestServicesContext || 'standard'} onValueChange={setGuestServicesContext}>
                                <SelectTrigger data-testid="entry-context-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="standard" data-testid="entry-context-opt-standard">{t('cardSettings.entryStandard', 'Standard')}</SelectItem>
                                    <SelectItem value="pool_terrace" data-testid="entry-context-opt-pool">{t('cardSettings.entryPool', 'Pool & Terrace')}</SelectItem>
                                    <SelectItem value="lobby" data-testid="entry-context-opt-lobby">{t('cardSettings.entryLobby', 'Lobby')}</SelectItem>
                                    <SelectItem value="fitness" data-testid="entry-context-opt-fitness">{t('cardSettings.entryFitness', 'Fitness Center')}</SelectItem>
                                </SelectContent>
                            </Select>

                            {/* Pool & Terrace — the only context with contextual configuration today */}
                            {guestServicesContext === 'pool_terrace' && (
                                <div className="space-y-4 border-t border-teal-500/20 pt-4" data-testid="pool-terrace-config">
                                    <p className="text-xs font-semibold text-teal-700 dark:text-teal-300 uppercase tracking-wide">{t('cardSettings.poolServicesTitle', 'Pool & Terrace services')}</p>
                                    {[
                                        { key: 'drinks', label: t('cardSettings.poolDrinks', 'Drinks'), val: poolDrinksEnabled, set: setPoolDrinksEnabled },
                                        { key: 'food', label: t('cardSettings.poolFood', 'Food'), val: poolFoodEnabled, set: setPoolFoodEnabled },
                                        { key: 'towels', label: t('cardSettings.poolTowels', 'Towels'), val: poolTowelsEnabled, set: setPoolTowelsEnabled },
                                        { key: 'other', label: t('cardSettings.poolOther', 'Other Pool Service'), val: poolOtherEnabled, set: setPoolOtherEnabled },
                                    ].map((s) => (
                                        <div key={s.key} className="flex items-center justify-between">
                                            <Label className="text-sm">{s.label}</Label>
                                            <Switch checked={s.val !== false} onCheckedChange={s.set} data-testid={`pool-toggle-${s.key}`} />
                                        </div>
                                    ))}

                                    {/* Drink Menu image */}
                                    <div className="border-t border-teal-500/20 pt-3 space-y-2">
                                        <Label className="text-sm flex items-center gap-2"><Image className="w-4 h-4" />{t('cardSettings.poolDrinkMenu', 'Drink Menu image')} <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                        {poolDrinkMenuImageUrl && (
                                            <div className="relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                                                <img src={poolDrinkMenuImageUrl} alt="" className="block w-full h-28 object-cover" onError={(e) => { e.target.style.opacity = '0.3'; }} data-testid="pool-drink-menu-preview" />
                                            </div>
                                        )}
                                        <div className="flex gap-2">
                                            <input ref={poolDrinkMenuInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handlePoolDrinkMenuUpload} className="hidden" data-testid="pool-drink-menu-file-input" />
                                            <Button type="button" variant="outline" size="sm" onClick={() => poolDrinkMenuInputRef?.current?.click()} disabled={uploadingLogo} className="flex-1" data-testid="upload-pool-drink-menu-btn">
                                                {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.uploading', 'Uploading...')}</>) : (<><Upload className="w-4 h-4 mr-2" />{t('cardSettings.uploadImage', 'Upload Image')}</>)}
                                            </Button>
                                            {poolDrinkMenuImageUrl && (
                                                <Button type="button" variant="outline" size="sm" onClick={() => setPoolDrinkMenuImageUrl('')} data-testid="pool-drink-menu-clear-btn"><X className="w-4 h-4" /></Button>
                                            )}
                                        </div>
                                        <Input value={poolDrinkMenuImageUrl || ''} onChange={(e) => setPoolDrinkMenuImageUrl(e.target.value)} placeholder="https://example.com/drink-menu.jpg" data-testid="pool-drink-menu-url-input" />
                                    </div>

                                    {/* Food Menu image */}
                                    <div className="border-t border-teal-500/20 pt-3 space-y-2">
                                        <Label className="text-sm flex items-center gap-2"><Image className="w-4 h-4" />{t('cardSettings.poolFoodMenu', 'Food Menu image')} <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                        {poolFoodMenuImageUrl && (
                                            <div className="relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                                                <img src={poolFoodMenuImageUrl} alt="" className="block w-full h-28 object-cover" onError={(e) => { e.target.style.opacity = '0.3'; }} data-testid="pool-food-menu-preview" />
                                            </div>
                                        )}
                                        <div className="flex gap-2">
                                            <input ref={poolFoodMenuInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handlePoolFoodMenuUpload} className="hidden" data-testid="pool-food-menu-file-input" />
                                            <Button type="button" variant="outline" size="sm" onClick={() => poolFoodMenuInputRef?.current?.click()} disabled={uploadingLogo} className="flex-1" data-testid="upload-pool-food-menu-btn">
                                                {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.uploading', 'Uploading...')}</>) : (<><Upload className="w-4 h-4 mr-2" />{t('cardSettings.uploadImage', 'Upload Image')}</>)}
                                            </Button>
                                            {poolFoodMenuImageUrl && (
                                                <Button type="button" variant="outline" size="sm" onClick={() => setPoolFoodMenuImageUrl('')} data-testid="pool-food-menu-clear-btn"><X className="w-4 h-4" /></Button>
                                            )}
                                        </div>
                                        <Input value={poolFoodMenuImageUrl || ''} onChange={(e) => setPoolFoodMenuImageUrl(e.target.value)} placeholder="https://example.com/food-menu.jpg" data-testid="pool-food-menu-url-input" />
                                    </div>
                                </div>
                            )}

                            {(guestServicesContext === 'lobby' || guestServicesContext === 'fitness') && (
                                <div className="border-t border-teal-500/20 pt-3" data-testid="entry-context-pending-note">
                                    <p className="text-xs text-muted-foreground">
                                        {t('cardSettings.entryPendingNote', 'This context is reserved. Its guest experience will be defined in a later update — for now the card behaves like Standard Guest Services.')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}



                    {/* Review Shield active indicator */}
                    {interactionMode === 'promo' && (
                        <CardPromoPanel
                            cardId={selectedCard?.card_id}
                            organizationId={selectedCard?.organization_id}
                            logoUrl={reviewShieldLogoUrl}
                            setLogoUrl={setReviewShieldLogoUrl}
                            backgroundUrl={reviewShieldBackgroundImageUrl}
                            setBackgroundUrl={setReviewShieldBackgroundImageUrl}
                            onUploadLogo={handleReviewShieldLogoUpload}
                            onUploadWallpaper={handleReviewShieldWallpaperUpload}
                            logoInputRef={reviewShieldLogoInputRef}
                            wallpaperInputRef={reviewShieldWallpaperInputRef}
                            uploading={uploadingLogo}
                            promoId={promoId}
                            setPromoId={setPromoId}
                            onApplied={setPromoIdSaved}
                            onSecondButtonChange={(cfg) => {
                                promoSecondRef.current = cfg;
                                setPromoSecondDirty(!!cfg.dirty);
                            }}
                        />
                    )}

                    {interactionMode === 'review_shield' && (
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 space-y-3">
                            <div>
                                <p className="text-sm text-emerald-800 dark:text-emerald-300 font-medium flex items-center gap-2">
                                    <Shield className="w-4 h-4" />
                                    {t('cardSettings.reviewShieldActive')}
                                </p>
                                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                                    {t('cardSettings.reviewShieldActiveDesc')}
                                </p>
                            </div>
                            {/* v4.34.4 — Combo toggle relocated here from the
                                Global Review section so it surfaces as a sub-option
                                of Review Shield rather than a hidden side-effect
                                of two independent switches. */}
                            {_p('card_global_review') && (
                                <div className="flex items-center justify-between pt-2 border-t border-emerald-200 dark:border-emerald-800">
                                    <div className="space-y-0.5 pr-3">
                                        <Label className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                                            {t('globalReview.useWithReviewShield', 'Use with Global Review')}
                                        </Label>
                                        <p className="text-xs text-emerald-700 dark:text-emerald-300">
                                            {t('globalReview.useWithReviewShieldDesc', 'Show destinations after positive ratings instead of a single review link')}
                                        </p>
                                    </div>
                                    <Switch
                                        checked={useReviewShieldWithGlobalReview}
                                        onCheckedChange={(v) => {
                                            setUseReviewShieldWithGlobalReview(v);
                                            setGlobalReviewEnabled(v);
                                        }}
                                        data-testid="use-review-shield-with-global-review-switch"
                                    />
                                </div>
                            )}
                            {/* Private Review — same Switch pattern and the same card
                                settings save path as the Global Review combo toggle
                                above. OFF = today's behaviour, byte-for-byte. */}
                            <div className="flex items-center justify-between pt-2 border-t border-emerald-200 dark:border-emerald-800" data-testid="private-review-section">
                                <div className="space-y-0.5 pr-3">
                                    <Label className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                                        {t('reviewShield.privateReview', 'Private Review')}
                                    </Label>
                                    <p className="text-xs text-emerald-700 dark:text-emerald-300">
                                        {t('reviewShield.privateReviewDesc', 'Keep every rating private — no guest is ever shown a public review link. Staff alerts for low ratings continue as usual.')}
                                    </p>
                                </div>
                                <Switch
                                    checked={!!privateReviewEnabled}
                                    onCheckedChange={(v) => setPrivateReviewEnabled(!!v)}
                                    data-testid="private-review-switch"
                                />
                            </div>
                        </div>
                    )}

                    {/* v4.34.4 — Drift warning removed: the Interaction Mode
                        dropdown now natively includes "Global Review" as a
                        4th option, making Standard + Global Review mutually
                        exclusive. The banner from v4.34.3 is no longer
                        reachable because effectiveMode collapses the four
                        underlying flags into a single dropdown value. */}

                    {/* ============ v3.3.1 — Languages (per-card override) — always visible ============ */}
                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/60 dark:bg-slate-900/40 space-y-2" data-testid="card-enabled-languages-section">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">{t('languages.enabledLanguages', 'Enabled Languages')}</Label>
                            {Array.isArray(enabledLanguages) && (
                                <button
                                    type="button"
                                    onClick={() => setEnabledLanguages(null)}
                                    className="text-xs text-fuchsia-600 dark:text-fuchsia-400 hover:underline"
                                    data-testid="card-enabled-languages-reset"
                                >
                                    {t('languages.resetToOrg', 'Reset to organization default')}
                                </button>
                            )}
                        </div>
                        <EnabledLanguagesPicker
                            value={enabledLanguages ?? null}
                            onChange={setEnabledLanguages}
                            allowInherit
                            inheritedFrom={['en','fr','es']}
                            testIdPrefix="card-languages"
                        />
                    </div>

                    {/* ============ GLOBAL REVIEW (sub-settings) ============
                        v4.34.4 — Standalone Switch removed; the Interaction Mode
                        dropdown is now the master toggle. This section appears
                        when the effective mode is 'global_review' OR when the
                        user has enabled the Review Shield + Global Review combo
                        from inside the Review Shield panel. */}
                    {_p('card_global_review') && (effectiveMode === 'global_review' || (effectiveMode === 'review_shield' && useReviewShieldWithGlobalReview)) && (
                    <div className="bg-slate-50 dark:bg-slate-700/40 rounded-lg p-3 space-y-3 border border-slate-200 dark:border-slate-600" data-testid="global-review-section">
                        <div className="space-y-0.5">
                            <Label className="text-sm font-semibold flex items-center gap-2 text-slate-900 dark:text-white">
                                <Globe className="w-4 h-4" />
                                {t('globalReview.title', 'Global Review')}
                            </Label>
                            <p className="text-xs text-slate-500 dark:text-slate-300">
                                {t('globalReview.description', 'Let guests choose where to engage (Google, Facebook, Instagram, etc.)')}
                            </p>
                        </div>

                        <div className="space-y-3 animate-in fade-in duration-200">
                                {effectiveMode === 'review_shield' && useReviewShieldWithGlobalReview && (
                                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-2.5">
                                        <p className="text-xs text-emerald-700 dark:text-emerald-300">
                                            {t('globalReview.comboInfo', 'Active inside Review Shield: guests who leave a positive rating will see these destinations.')}
                                        </p>
                                    </div>
                                )}

                                {effectiveMode === 'global_review' && (
                                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-2.5">
                                        <p className="text-xs text-emerald-700 dark:text-emerald-300">
                                            {t('globalReview.standaloneInfo', 'Global Review is active as a standalone destination page. Guests will see destination options after tapping.')}
                                        </p>
                                    </div>
                                )}

                                {/* Destination visibility toggles */}
                                <div>
                                    <Label className="text-sm font-medium mb-2 block text-slate-800 dark:text-slate-100">
                                        {t('globalReview.destinations', 'Destination Visibility')}
                                    </Label>
                                    <p className="text-xs text-slate-500 dark:text-slate-300 mb-2">
                                        {t('globalReview.destinationsHint', 'Only platforms with configured URLs in your organization settings will appear. Toggle visibility per destination.')}
                                    </p>
                                    <div className="space-y-1.5">
                                        {[
                                            { key: 'google_reviews', label: 'Google Reviews' },
                                            { key: 'facebook', label: 'Facebook' },
                                            { key: 'instagram', label: 'Instagram' },
                                            { key: 'trustpilot', label: 'Trustpilot' },
                                            { key: 'yelp', label: 'Yelp' },
                                            { key: 'tripadvisor', label: 'TripAdvisor' },
                                            // MEDICAL ONLY — every other vertical keeps the list above.
                                            ...(isMedicalOrg ? [{ key: 'ratemds', label: 'RateMDs' }] : [])
                                        ].map((platform) => (
                                            <div key={platform.key} className="flex items-center justify-between py-1.5 px-2 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600">
                                                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{platform.label}</span>
                                                <Switch
                                                    checked={globalReviewDestinations?.[platform.key] !== false}
                                                    onCheckedChange={(checked) => {
                                                        setGlobalReviewDestinations(prev => ({
                                                            ...(prev || {}),
                                                            [platform.key]: checked
                                                        }));
                                                    }}
                                                    data-testid={`global-review-dest-toggle-${platform.key}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* === Global Review Logo / Image === */}
                                <div className="border-t border-slate-200 dark:border-slate-600 pt-3">
                                    <Label className="mb-1 block text-sm text-slate-800 dark:text-slate-100">
                                        <div className="flex items-center gap-2"><Image className="w-4 h-4" />{t('globalReview.logoLabel', 'Global Review Logo')} <span className="text-slate-500 dark:text-slate-400 font-normal">(optional)</span></div>
                                    </Label>
                                    <p className="text-xs text-slate-500 dark:text-slate-300 mb-2">
                                        {t('globalReview.logoHint', 'If not set, the Review Shield logo or organization default will be used automatically.')}
                                    </p>
                                    {globalReviewLogoUrl && (
                                        <div className="mb-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border relative">
                                            <img src={globalReviewLogoUrl} alt="Global Review Logo" className="max-h-16 max-w-[150px] object-contain mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                                            <button type="button" onClick={() => setGlobalReviewLogoUrl('')} className="absolute top-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-600" title="Remove logo" data-testid="global-review-clear-logo">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex gap-2 mb-2">
                                        <input ref={globalReviewLogoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml" onChange={handleGlobalReviewLogoUpload} className="hidden" data-testid="global-review-logo-file-input" />
                                        <Button type="button" variant="outline" size="sm" onClick={() => globalReviewLogoInputRef.current?.click()} disabled={uploadingLogo} className="flex-1" data-testid="upload-global-review-logo-btn">
                                            {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4 mr-2" />Upload Image</>)}
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-2 my-2">
                                        <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                        <span className="text-xs text-slate-500 dark:text-slate-400">{t('cardSettings.or')}</span>
                                        <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                    </div>
                                    <Input
                                        value={globalReviewLogoUrl || ''}
                                        onChange={(e) => setGlobalReviewLogoUrl(e.target.value)}
                                        placeholder="https://example.com/logo.png"
                                        data-testid="global-review-logo-url-input"
                                    />
                                    <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">Upload an image (PNG, JPEG, GIF, WebP, SVG - max 5MB) or enter a URL</p>
                                    {globalReviewLogoUrl && (
                                        <div className="mt-3">
                                            <Label className="mb-2 block text-sm text-slate-800 dark:text-slate-100">Logo Size</Label>
                                            <div className="flex gap-2">
                                                {[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }, { value: 'very_large', label: 'Extra Large' }].map((size) => (
                                                    <Button key={size.value} type="button" variant={globalReviewLogoSize === size.value ? "default" : "outline"} size="sm" onClick={() => setGlobalReviewLogoSize(size.value)} className="flex-1" data-testid={`global-review-logo-size-${size.value}`}>{size.label}</Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* === v3.5.18 — Wallpaper / Background image === */}
                                <div className="space-y-2 border-t border-slate-200 dark:border-slate-600 pt-3" data-testid="global-review-wallpaper-block">
                                    <Label className="text-sm font-medium flex items-center gap-2 text-slate-800 dark:text-slate-100">
                                        <Image className="w-4 h-4" />
                                        {t('globalReview.wallpaperLabel', 'Background image (wallpaper)')}
                                    </Label>
                                    <p className="text-xs text-slate-500 dark:text-slate-300">
                                        {t('globalReview.wallpaperHelp', 'Optional. Applied across the language gate AND review screens. Leave empty to inherit the organization brand wallpaper, or fall back to the default dark gradient.')}
                                    </p>
                                    <div className="flex gap-2">
                                        <input
                                            ref={globalReviewWallpaperInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                            onChange={handleGlobalReviewWallpaperUpload}
                                            className="hidden"
                                            data-testid="global-review-wallpaper-file-input"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => globalReviewWallpaperInputRef?.current?.click()}
                                            disabled={uploadingLogo}
                                            className="flex-1"
                                            data-testid="upload-global-review-wallpaper-btn"
                                        >
                                            {uploadingLogo
                                                ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.uploading', 'Uploading...')}</>)
                                                : (<><Upload className="w-4 h-4 mr-2" />{t('globalReview.uploadWallpaperBtn', 'Upload wallpaper')}</>)
                                            }
                                        </Button>
                                        {globalReviewBackgroundImageUrl && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setGlobalReviewBackgroundImageUrl('')}
                                                data-testid="global-review-wallpaper-clear-btn"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                    <Input
                                        value={globalReviewBackgroundImageUrl || ''}
                                        onChange={(e) => setGlobalReviewBackgroundImageUrl(e.target.value)}
                                        placeholder={t('globalReview.wallpaperOrPaste', 'or paste a hosted image URL (https://…)')}
                                        data-testid="global-review-wallpaper-url-input"
                                    />
                                    {globalReviewBackgroundImageUrl && (
                                        <div className="relative mt-2 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
                                            <img
                                                src={globalReviewBackgroundImageUrl}
                                                alt=""
                                                className="block w-full h-32 object-cover"
                                                onError={(e) => { e.target.style.opacity = '0.3'; }}
                                                data-testid="global-review-wallpaper-preview"
                                            />
                                            <div className="absolute inset-0 bg-black/50 pointer-events-none flex items-center justify-center text-white text-xs">
                                                {t('globalReview.wallpaperPreviewOverlay', 'Same overlay applied at runtime for readability')}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* === Custom Message === */}
                                <div className="border-t border-slate-200 dark:border-slate-600 pt-3">
                                    <Label className="mb-1 block text-sm text-slate-800 dark:text-slate-100">
                                        {t('globalReview.customMessage', 'Custom Message')} <span className="text-slate-500 dark:text-slate-400 font-normal text-xs">(optional)</span>
                                    </Label>
                                    <p className="text-xs text-slate-500 dark:text-slate-300 mb-2">
                                        {t('globalReview.customMessageHint', 'Appears above the platform selection buttons. Supports English and French.')}
                                    </p>
                                    <div className="space-y-2">
                                        <div>
                                            <Label className="text-xs text-slate-600 dark:text-slate-300 mb-0.5 block">English</Label>
                                            <Textarea
                                                value={globalReviewMessageEn || ''}
                                                onChange={(e) => setGlobalReviewMessageEn(e.target.value)}
                                                placeholder="Thank you for your visit — choose where you'd like to connect with us."
                                                rows={2}
                                                className="text-sm"
                                                data-testid="global-review-message-en"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-600 dark:text-slate-300 mb-0.5 block">Fran&ccedil;ais</Label>
                                            <Textarea
                                                value={globalReviewMessageFr || ''}
                                                onChange={(e) => setGlobalReviewMessageFr(e.target.value)}
                                                placeholder="Merci de votre visite — choisissez votre plateforme pr&eacute;f&eacute;r&eacute;e."
                                                rows={2}
                                                className="text-sm"
                                                data-testid="global-review-message-fr"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                    </div>
                    )}

                    {/* Landing Page Settings - only show when advertising mode */}
                    {interactionMode === 'advertising' && (
                        <>
                            <div className="border-t pt-3 space-y-3">
                                {/* Show Text Toggle */}
                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <Label className="text-sm font-medium">{t('cardSettings.showTextOnLanding')}</Label>
                                        <p className="text-xs text-muted-foreground">
                                            {landingShowText ? t('cardSettings.textVisible') : t('cardSettings.textHidden')}
                                        </p>
                                    </div>
                                    <Switch checked={landingShowText} onCheckedChange={setLandingShowText} data-testid="landing-show-text-switch" />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-sm">{t('cardSettings.customHeadline')} <span className="text-muted-foreground ml-1 font-normal">{t('cardSettings.optional')}</span></Label>
                                    <Input value={landingHeadline} onChange={(e) => setLandingHeadline(e.target.value)} placeholder={t('cardSettings.headlinePlaceholder')} data-testid="landing-headline-input" />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-sm">{t('cardSettings.customMessage')} <span className="text-muted-foreground ml-1 font-normal">{t('cardSettings.optional')}</span></Label>
                                    <Textarea value={landingDescription} onChange={(e) => setLandingDescription(e.target.value)} placeholder={t('cardSettings.messagePlaceholder')} rows={2} data-testid="landing-description-input" />
                                </div>

                                {/* Logo */}
                                <div>
                                    <Label className="mb-1 block text-sm">
                                        <div className="flex items-center gap-2"><Image className="w-4 h-4" />{t('cardSettings.logo')} <span className="text-muted-foreground font-normal">{t('cardSettings.optional')}</span></div>
                                    </Label>
                                    {landingLogoUrl && (
                                        <div className="mb-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border relative">
                                            <img src={landingLogoUrl} alt="Logo preview" className="max-h-16 max-w-[150px] object-contain mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                                            <button type="button" onClick={clearLogo} className="absolute top-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-600" title="Remove logo">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex gap-2 mb-2">
                                        <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                                        <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} className="flex-1" data-testid="upload-logo-btn">
                                            {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('cardSettings.uploading')}</>) : (<><Upload className="w-4 h-4 mr-2" />{t('cardSettings.uploadImage')}</>)}
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-2 my-2">
                                        <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                        <span className="text-xs text-muted-foreground">{t('cardSettings.or')}</span>
                                        <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                    </div>
                                    <Input value={landingLogoUrl} onChange={(e) => setLandingLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" data-testid="landing-logo-input" />
                                    <p className="text-xs text-muted-foreground mt-1">{t('cardSettings.uploadHint')}</p>
                                    {landingLogoUrl && (
                                        <div className="mt-3">
                                            <Label className="mb-2 block text-sm">{t('cardSettings.logoSize')}</Label>
                                            <div className="flex gap-2">
                                                {[{ value: 'small', label: t('cardSettings.small') }, { value: 'medium', label: t('cardSettings.medium') }, { value: 'large', label: t('cardSettings.large') }, { value: 'very_large', label: t('cardSettings.veryLarge') }].map((size) => (
                                                    <Button key={size.value} type="button" variant={landingLogoSize === size.value ? "default" : "outline"} size="sm" onClick={() => setLandingLogoSize(size.value)} className="flex-1" data-testid={`logo-size-${size.value}`}>{size.label}</Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ============ VIDEO MEDIA UPLOAD ============ */}
                                <div className="border-t pt-3 mt-2" data-testid="video-upload-section">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-medium flex items-center gap-2">
                                                <Film className="w-4 h-4" />
                                                {t('cardSettings.videoMedia')}
                                            </Label>
                                            <p className="text-xs text-muted-foreground">
                                                {t('cardSettings.videoMediaDesc')}
                                            </p>
                                        </div>
                                        <Switch
                                            checked={landingVideoEnabled}
                                            onCheckedChange={(checked) => {
                                                setLandingVideoEnabled(checked);
                                                if (checked && mediaVideoUrl) {
                                                    setMediaType('video');
                                                }
                                                if (!checked) {
                                                    setMediaType(null);
                                                }
                                            }}
                                            data-testid="landing-video-enabled-switch"
                                        />
                                    </div>

                                    {landingVideoEnabled && (
                                        <div className="space-y-3 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                            {/* Video upload / preview */}
                                            {mediaVideoUrl ? (
                                                <div className="relative rounded-lg overflow-hidden border bg-black">
                                                    <video
                                                        src={mediaVideoUrl.startsWith('/api') ? `${window.location.origin}${mediaVideoUrl}` : mediaVideoUrl}
                                                        className="w-full max-h-[160px] object-contain"
                                                        controls
                                                        muted
                                                        data-testid="video-preview"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={removeVideo}
                                                        className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/80 hover:bg-red-600 text-white"
                                                        title="Remove video"
                                                        data-testid="remove-video-btn"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <input
                                                        ref={videoInputRef}
                                                        type="file"
                                                        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
                                                        onChange={handleVideoUpload}
                                                        className="hidden"
                                                        data-testid="video-file-input"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => videoInputRef?.current?.click()}
                                                        disabled={uploadingVideo}
                                                        className="w-full"
                                                        data-testid="upload-video-btn"
                                                    >
                                                        {uploadingVideo ? (
                                                            <>
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                {videoUploadProgress >= 100 ? 'Processing video...' : `Uploading... ${videoUploadProgress > 0 ? `${videoUploadProgress}%` : ''}`}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Upload className="w-4 h-4 mr-2" />
                                                                {t('cardSettings.uploadVideo')}
                                                            </>
                                                        )}
                                                    </Button>
                                                    {uploadingVideo && videoUploadProgress > 0 && (
                                                        <div className="mt-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5">
                                                            <div
                                                                className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                                                                style={{ width: `${videoUploadProgress}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                    <p className="text-xs text-muted-foreground mt-1.5">
                                                        {t('cardSettings.videoFormats')}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Playback Controls */}
                                            {mediaVideoUrl && (
                                                <div className="space-y-2 border-t pt-3">
                                                    <Label className="text-sm font-medium flex items-center gap-2">
                                                        <Settings className="w-3.5 h-3.5" />
                                                        {t('cardSettings.playbackControls')}
                                                    </Label>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="flex items-center justify-between py-1.5 px-2 rounded-md bg-white dark:bg-slate-900 border">
                                                            <span className="text-xs font-medium flex items-center gap-1.5">
                                                                <Play className="w-3 h-3" /> {t('cardSettings.autoplay')}
                                                            </span>
                                                            <Switch
                                                                checked={mediaAutoplay}
                                                                onCheckedChange={setMediaAutoplay}
                                                                data-testid="media-autoplay-switch"
                                                            />
                                                        </div>
                                                        <div className="flex items-center justify-between py-1.5 px-2 rounded-md bg-white dark:bg-slate-900 border">
                                                            <span className="text-xs font-medium">{t('cardSettings.loop')}</span>
                                                            <Switch
                                                                checked={mediaLoop}
                                                                onCheckedChange={setMediaLoop}
                                                                data-testid="media-loop-switch"
                                                            />
                                                        </div>
                                                        <div className="flex items-center justify-between py-1.5 px-2 rounded-md bg-white dark:bg-slate-900 border">
                                                            <span className="text-xs font-medium flex items-center gap-1.5">
                                                                <VolumeX className="w-3 h-3" /> {t('cardSettings.muted')}
                                                            </span>
                                                            <Switch
                                                                checked={mediaMuted}
                                                                onCheckedChange={setMediaMuted}
                                                                data-testid="media-muted-switch"
                                                            />
                                                        </div>
                                                        <div className="flex items-center justify-between py-1.5 px-2 rounded-md bg-white dark:bg-slate-900 border">
                                                            <span className="text-xs font-medium flex items-center gap-1.5">
                                                                <SkipForward className="w-3 h-3" /> {t('cardSettings.skip')}
                                                            </span>
                                                            <Switch
                                                                checked={mediaSkipEnabled}
                                                                onCheckedChange={setMediaSkipEnabled}
                                                                data-testid="media-skip-switch"
                                                            />
                                                        </div>
                                                    </div>
                                                    {mediaAutoplay && !mediaMuted && (
                                                        <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                                                            {t('cardSettings.autoplayNote')}
                                                        </p>
                                                    )}
                                                    {mediaSkipEnabled && (
                                                        <div className="flex items-center gap-2">
                                                            <Label className="text-xs whitespace-nowrap">{t('cardSettings.skipDelay')}</Label>
                                                            <Input
                                                                type="number"
                                                                min={1}
                                                                max={30}
                                                                value={mediaSkipDelay}
                                                                onChange={(e) => setMediaSkipDelay(Math.max(1, Math.min(30, parseInt(e.target.value) || 5)))}
                                                                className="w-20 h-7 text-sm"
                                                                data-testid="media-skip-delay-input"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Fallback Image */}
                                            {mediaVideoUrl && (
                                                <div className="border-t pt-3">
                                                    <Label className="text-xs mb-1 block">{t('cardSettings.fallbackImageUrl')} <span className="text-muted-foreground font-normal">({t('cardSettings.optional')})</span></Label>
                                                    <Input
                                                        value={mediaVideoFallbackImage || ''}
                                                        onChange={(e) => setMediaVideoFallbackImage(e.target.value)}
                                                        placeholder="https://example.com/fallback.jpg"
                                                        className="text-sm"
                                                        data-testid="video-fallback-image-input"
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">{t('cardSettings.fallbackImageHint')}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Multiple Ads Feature */}
                                {isSuperAdmin() && (
                                    <div className="border-t pt-3 mt-2">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="space-y-0.5">
                                                <Label className="text-sm font-medium flex items-center gap-2"><Images className="w-4 h-4" />{t('cardSettings.multipleAds')}</Label>
                                                <p className="text-xs text-muted-foreground">{t('cardSettings.multipleAdsDesc')}</p>
                                            </div>
                                            <Switch checked={multipleAdsEnabled} onCheckedChange={setMultipleAdsEnabled} data-testid="multiple-ads-switch" />
                                        </div>
                                        {multipleAdsEnabled && (
                                            <div className="space-y-3 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Label className="text-sm font-medium">{t('cardSettings.sameTextForAll')}</Label>
                                                    <Switch checked={sameTextForAll} onCheckedChange={setSameTextForAll} data-testid="same-text-switch" />
                                                </div>
                                                <div className="space-y-2">
                                                    {(adImages || []).map((img, index) => (
                                                        <div key={index} className="flex items-start gap-2 bg-white dark:bg-slate-900 p-2 rounded border">
                                                            <div className="flex-1 space-y-1">
                                                                <div className="flex items-center gap-2">
                                                                    <img src={img} alt={`Ad ${index + 1}`} className="w-12 h-12 object-cover rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                                                                    <span className="text-xs text-muted-foreground">{t('cardSettings.adLabel', { index: index + 1 })}</span>
                                                                </div>
                                                                {!sameTextForAll && (
                                                                    <div className="mt-1 space-y-1">
                                                                        <Input size="sm" placeholder={t('cardSettings.headline')} value={adTexts?.[index]?.headline || ''} onChange={(e) => updateAdText(index, 'headline', e.target.value)} className="text-sm h-7" />
                                                                        <Input size="sm" placeholder={t('cardSettings.descriptionText')} value={adTexts?.[index]?.description || ''} onChange={(e) => updateAdText(index, 'description', e.target.value)} className="text-sm h-7" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <Button type="button" variant="ghost" size="sm" onClick={() => removeAdImage(index)} className="text-red-500 hover:text-red-700">
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                    {(adImages || []).length < 5 && (
                                                        <div>
                                                            <input ref={adImageInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleAdImageUpload} className="hidden" />
                                                            <Button type="button" variant="outline" size="sm" onClick={() => adImageInputRef.current?.click()} className="w-full" data-testid="add-ad-image-btn">
                                                                <Plus className="w-4 h-4 mr-2" />{t('cardSettings.addImage', { count: (adImages || []).length })}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Redirect Settings */}
                                <div className="border-t pt-3">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-medium">{t('cardSettings.autoRedirect')}</Label>
                                            <p className="text-xs text-muted-foreground">{t('cardSettings.autoRedirectDesc')}</p>
                                        </div>
                                        <Switch checked={landingAutoRedirect} onCheckedChange={setLandingAutoRedirect} data-testid="landing-auto-redirect-switch" />
                                    </div>
                                    {landingAutoRedirect && (
                                        <div className="space-y-3">
                                            <div>
                                                <Label className="mb-1 block text-sm">{t('cardSettings.redirectDelay')}</Label>
                                                <Input type="number" min="1" max="30" value={landingRedirectDelay} onChange={(e) => setLandingRedirectDelay(parseInt(e.target.value) || 5)} data-testid="landing-redirect-delay-input" />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-0.5">
                                                    <Label className="text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4" />{t('cardSettings.countdownClock')}</Label>
                                                    <p className="text-xs text-muted-foreground">{t('cardSettings.countdownClockDesc')}</p>
                                                </div>
                                                <Switch checked={countdownClockEnabled} onCheckedChange={setCountdownClockEnabled} data-testid="countdown-clock-switch" />
                                            </div>
                                            {countdownClockEnabled && (
                                                <div>
                                                    <Label className="mb-1 block text-sm">{t('cardSettings.clockColor')}</Label>
                                                    <div className="flex gap-2">
                                                        {['blue', 'green', 'red', 'purple', 'orange'].map((color) => (
                                                            <button key={color} type="button" onClick={() => setCountdownClockColor(color)}
                                                                className={`w-8 h-8 rounded-full border-2 ${countdownClockColor === color ? 'border-foreground scale-110' : 'border-transparent'}`}
                                                                style={{ backgroundColor: { blue: '#3b82f6', green: '#10b981', red: '#ef4444', purple: '#8b5cf6', orange: '#f97316' }[color] }}
                                                                data-testid={`clock-color-${color}`} />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Branding */}
                                <div className="border-t pt-3">
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                            <Label className="text-sm font-medium">{t('cardSettings.showBranding')}</Label>
                                            <p className="text-xs text-muted-foreground">{t('cardSettings.showBrandingDesc')}</p>
                                        </div>
                                        <Switch checked={showYulontapBranding} onCheckedChange={setShowYulontapBranding} data-testid="yulontap-branding-switch" />
                                    </div>
                                </div>

                                {/* Schedule */}
                                {isSuperAdmin() && (
                                    <div className="border-t pt-3">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="space-y-0.5">
                                                <Label className="text-sm font-medium flex items-center gap-2"><Calendar className="w-4 h-4" />{t('cardSettings.schedule')}</Label>
                                                <p className="text-xs text-muted-foreground">{t('cardSettings.scheduleDesc')}</p>
                                            </div>
                                            <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} data-testid="schedule-enabled-switch" />
                                        </div>
                                        {scheduleEnabled && (
                                            <div className="space-y-3 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                                <div>
                                                    <Label className="mb-1 block text-sm">{t('cardSettings.timezone')}</Label>
                                                    <Select value={scheduleTimezone} onValueChange={setScheduleTimezone}>
                                                        <SelectTrigger data-testid="schedule-timezone-select"><SelectValue /></SelectTrigger>
                                                        <SelectContent>{TIMEZONES.map(tz => (<SelectItem key={tz} value={tz}>{tz}</SelectItem>))}</SelectContent>
                                                    </Select>
                                                </div>
                                                {(scheduleEntries || []).map((entry, index) => (
                                                    <div key={index} className="border rounded-lg p-2 bg-white dark:bg-slate-900 space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <Input value={entry.name || ''} onChange={(e) => updateScheduleEntry(index, 'name', e.target.value)} className="text-sm h-7 w-40" placeholder={t('cardSettings.scheduleName')} />
                                                            <Button type="button" variant="ghost" size="sm" onClick={() => removeScheduleEntry(index)} className="text-red-500"><X className="w-3 h-3" /></Button>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {DAYS_OF_WEEK.map(day => (
                                                                <button key={day.value} type="button" onClick={() => toggleScheduleDay(index, day.value)}
                                                                    className={`px-2 py-0.5 text-xs rounded ${(entry.days || []).includes(day.value) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                                                    {day.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        <div className="flex gap-2 items-center">
                                                            <Input type="time" value={entry.start_time || '09:00'} onChange={(e) => updateScheduleEntry(index, 'start_time', e.target.value)} className="text-sm h-7" />
                                                            <span className="text-xs text-muted-foreground">{t('cardSettings.to')}</span>
                                                            <Input type="time" value={entry.end_time || '17:00'} onChange={(e) => updateScheduleEntry(index, 'end_time', e.target.value)} className="text-sm h-7" />
                                                        </div>
                                                    </div>
                                                ))}
                                                <Button type="button" variant="outline" size="sm" onClick={addScheduleEntry} className="w-full" data-testid="add-schedule-btn">
                                                    <Plus className="w-4 h-4 mr-2" />{t('cardSettings.addSchedule')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Review Shield settings sections */}
                    {interactionMode === 'review_shield' && (
                        <div className="border-t pt-3 space-y-4">
                            {/* === Guest Experience Version (V2 visual enhancement) ===
                              * Presentation-only. V1 Classic (default) keeps the
                              * existing experience byte-identical. V2 Enhanced applies
                              * the premium visual treatment to the SAME flow / logic.
                              * All existing cards stay on V1 unless explicitly switched. */}
                            <div data-testid="guest-experience-version-block">
                                <Label className="text-sm font-medium">
                                    {t('guestExperience.label', 'Guest Experience')}
                                </Label>
                                <p className="text-xs text-muted-foreground mt-1 mb-2">
                                    {t('guestExperience.help', 'Choose how the guest-facing tap experience looks. Same flow and functionality — only the visual presentation changes.')}
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { value: 'v1', title: t('guestExperience.v1.title', 'Version 1 — Classic'), desc: t('guestExperience.v1.desc', 'The current experience') },
                                        { value: 'v2', title: t('guestExperience.v2.title', 'Version 2 — Enhanced'), desc: t('guestExperience.v2.desc', 'Premium visual upgrade') },
                                    ].map((opt) => {
                                        const active = (guestExperienceVersion || 'v1') === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => setGuestExperienceVersion(opt.value)}
                                                aria-pressed={active}
                                                data-testid={`guest-experience-version-${opt.value}`}
                                                className={`text-left rounded-xl border p-3 transition-all ${
                                                    active
                                                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-500'
                                                        : 'border-border hover:border-emerald-300'
                                                }`}
                                            >
                                                <span className="block text-sm font-semibold">{opt.title}</span>
                                                <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* === Experience Type — ORGANIZATION-WIDE ===
                              * This is a PORTAL/ORG-level setting: it governs the
                              * guest flow for EVERY Review Shield card in this
                              * organization. Changing it saves immediately to the
                              * organization (no card "Save Changes" needed) and is
                              * NOT stored on this individual card. */}
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Label className="text-sm font-medium">
                                        {t('reviewShield.experience.label')}
                                    </Label>
                                    <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="experience-type-org-badge">
                                        {t('reviewShield.experience.orgWideBadge', 'Organization-wide')}
                                    </span>
                                    {savingExperienceType && (
                                        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid="experience-type-saving">
                                            <Loader2 className="w-3 h-3 animate-spin" /> {t('common.saving', 'Saving…')}
                                        </span>
                                    )}
                                </div>
                                <Select value={experienceType || (isMedicalOrg ? 'medical' : 'dine_in')} onValueChange={onExperienceTypeChange} disabled={savingExperienceType}>
                                    <SelectTrigger data-testid="review-shield-experience-type-select" data-insight-id="review-shield-experience-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {/* MEDICAL — a clinic is one experience. It is the ONLY
                                            option for a medical organization; every other vertical
                                            keeps exactly the options it has today. */}
                                        {isMedicalOrg ? (
                                            <SelectItem value="medical" data-testid="review-shield-experience-opt-medical">
                                                <span className="flex items-center gap-2">
                                                    <Stethoscope className="w-4 h-4" aria-hidden="true" />
                                                    <span>{t('reviewShield.experience.product.medical.name', 'Review Shield — Medical Clinic')}</span>
                                                </span>
                                            </SelectItem>
                                        ) : (<>
                                        <SelectItem value="dine_in" data-testid="review-shield-experience-opt-dine_in">
                                            <span className="flex items-center gap-2">
                                                <span aria-hidden="true">{t('reviewShield.experience.dine_in_emoji')}</span>
                                                <span>{t('reviewShield.experience.product.dine_in.name')}</span>
                                            </span>
                                        </SelectItem>
                                        <SelectItem value="pickup" data-testid="review-shield-experience-opt-pickup">
                                            <span className="flex items-center gap-2">
                                                <span aria-hidden="true">{t('reviewShield.experience.pickup_emoji')}</span>
                                                <span>{t('reviewShield.experience.product.pickup.name')}</span>
                                            </span>
                                        </SelectItem>
                                        <SelectItem value="delivery" data-testid="review-shield-experience-opt-delivery">
                                            <span className="flex items-center gap-2">
                                                <span aria-hidden="true">{t('reviewShield.experience.delivery_emoji')}</span>
                                                <span>{t('reviewShield.experience.product.delivery.name')}</span>
                                            </span>
                                        </SelectItem>
                                        {isAutomotiveOrg && (
                                            <SelectItem value="automotive" data-testid="review-shield-experience-opt-automotive">
                                                <span className="flex items-center gap-2">
                                                    <span aria-hidden="true">🚘</span>
                                                    <span>{t('reviewShield.experience.product.automotive.name', 'Review Shield — Automotive / Car Dealer')}</span>
                                                </span>
                                            </SelectItem>
                                        )}
                                        </>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t(`reviewShield.experience.product.${experienceType || (isMedicalOrg ? 'medical' : 'dine_in')}.tagline`, experienceType === 'automotive' ? 'Guests rate their dealership visit; low ratings surface your saved categories (Sales, Parts, F&I, …).' : '')}
                                </p>
                                <p className="text-[11px] text-muted-foreground/80 mt-1" data-testid="experience-type-org-note">
                                    {t('reviewShield.experience.orgWideNote', 'Applies to every Review Shield card in this organization. Saved instantly — no need to press Save Changes.')}
                                </p>
                            </div>

                            {/* === Photo attachments — ORGANIZATION-WIDE === */}
                            {photosEnabled !== null && (
                            <div data-testid="review-shield-photo-setting">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Label className="text-sm font-medium">{t('reviewShield.photoSetting.label')}</Label>
                                        <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                            {t('reviewShield.experience.orgWideBadge', 'Organization-wide')}
                                        </span>
                                        {savingPhotos && (
                                            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                                                <Loader2 className="w-3 h-3 animate-spin" /> {t('common.saving', 'Saving…')}
                                            </span>
                                        )}
                                    </div>
                                    <Switch
                                        checked={photosEnabled}
                                        disabled={savingPhotos}
                                        onCheckedChange={savePhotosEnabled}
                                        data-testid="review-shield-photo-setting-switch"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('reviewShield.photoSetting.help')}</p>
                            </div>
                            )}

                            {/* === Language Mode === */}
                            <div>
                                <Label className="text-sm font-medium">{t('reviewShield.languageMode', 'Language Mode')}</Label>
                                <Select value={languageMode || 'bilingual'} onValueChange={setLanguageMode}>
                                    <SelectTrigger data-testid="review-shield-language-mode-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="bilingual">{t('reviewShield.languageBilingual', 'Bilingual')}</SelectItem>
                                        <SelectItem value="en">{t('reviewShield.languageEnglishOnly', 'English Only')}</SelectItem>
                                        <SelectItem value="fr">{t('reviewShield.languageFrenchOnly', 'French Only')}</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {languageMode === 'bilingual' && t('cardSettings.languageBilingual')}
                                    {languageMode === 'en' && t('cardSettings.languageEn')}
                                    {languageMode === 'fr' && t('cardSettings.languageFr')}
                                </p>
                            </div>

                            {/* === Review Shield Logo === */}
                            <div>
                                <Label className="mb-1 block text-sm">
                                    <div className="flex items-center gap-2"><Image className="w-4 h-4" />{t('reviewShield.logoLabel', 'Review Shield Logo')} <span className="text-muted-foreground font-normal">(optional)</span></div>
                                </Label>
                                {reviewShieldLogoUrl && (
                                    <div className="mb-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border relative">
                                        <img src={reviewShieldLogoUrl} alt="Logo preview" className="max-h-16 max-w-[150px] object-contain mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                                        <button type="button" onClick={clearReviewShieldLogo} className="absolute top-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-600" title="Remove logo">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                                <div className="flex gap-2 mb-2">
                                    <input ref={reviewShieldLogoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml" onChange={handleReviewShieldLogoUpload} className="hidden" data-testid="review-shield-logo-file-input" />
                                    <Button type="button" variant="outline" size="sm" onClick={() => reviewShieldLogoInputRef.current?.click()} disabled={uploadingLogo} className="flex-1" data-testid="upload-review-shield-logo-btn">
                                        {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4 mr-2" />Upload Image</>)}
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2 my-2">
                                    <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                    <span className="text-xs text-muted-foreground">{t('cardSettings.or')}</span>
                                    <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                </div>
                                <Input value={reviewShieldLogoUrl || ''} onChange={(e) => setReviewShieldLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" data-testid="review-shield-logo-url-input" />
                                <p className="text-xs text-muted-foreground mt-1">Upload an image (PNG, JPEG, GIF, WebP, SVG - max 5MB) or enter a URL</p>
                                {reviewShieldLogoUrl && (
                                    <div className="mt-3">
                                        <Label className="mb-2 block text-sm">{t('reviewShield.logoSize', 'Logo Size')}</Label>
                                        <div className="flex gap-2">
                                            {[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }, { value: 'very_large', label: 'Extra Large' }].map((size) => (
                                                <Button key={size.value} type="button" variant={reviewShieldLogoSize === size.value ? "default" : "outline"} size="sm" onClick={() => setReviewShieldLogoSize(size.value)} className="flex-1" data-testid={`review-shield-logo-size-${size.value}`}>{size.label}</Button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* === v3.5.18 — Wallpaper / Background image === */}
                            <div className="space-y-2 border-t pt-4" data-testid="review-shield-wallpaper-block">
                                <Label className="text-sm font-medium flex items-center gap-2">
                                    <Image className="w-4 h-4" />
                                    {t('reviewShield.wallpaperLabel', 'Background image (wallpaper)')}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    {t('reviewShield.wallpaperHelp', 'Optional. Applied across the language gate AND review screens. Leave empty to inherit the organization brand wallpaper, or fall back to the default dark gradient.')}
                                </p>
                                <div className="flex gap-2">
                                    <input
                                        ref={reviewShieldWallpaperInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                        onChange={handleReviewShieldWallpaperUpload}
                                        className="hidden"
                                        data-testid="review-shield-wallpaper-file-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => reviewShieldWallpaperInputRef?.current?.click()}
                                        disabled={uploadingLogo}
                                        className="flex-1"
                                        data-testid="upload-review-shield-wallpaper-btn"
                                    >
                                        {uploadingLogo
                                            ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.uploading', 'Uploading...')}</>)
                                            : (<><Upload className="w-4 h-4 mr-2" />{t('reviewShield.uploadWallpaperBtn', 'Upload wallpaper')}</>)
                                        }
                                    </Button>
                                    {reviewShieldBackgroundImageUrl && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setReviewShieldBackgroundImageUrl('')}
                                            data-testid="review-shield-wallpaper-clear-btn"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                                <Input
                                    value={reviewShieldBackgroundImageUrl || ''}
                                    onChange={(e) => setReviewShieldBackgroundImageUrl(e.target.value)}
                                    placeholder={t('reviewShield.wallpaperOrPaste', 'or paste a hosted image URL (https://…)')}
                                    data-testid="review-shield-wallpaper-url-input"
                                />
                                {reviewShieldBackgroundImageUrl && (
                                    <div className="relative mt-2 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                                        <img
                                            src={reviewShieldBackgroundImageUrl}
                                            alt=""
                                            className="block w-full h-32 object-cover"
                                            onError={(e) => { e.target.style.opacity = '0.3'; }}
                                            data-testid="review-shield-wallpaper-preview"
                                        />
                                        <div className="absolute inset-0 bg-black/50 pointer-events-none flex items-center justify-center text-white text-xs">
                                            {t('reviewShield.wallpaperPreviewOverlay', 'Same overlay applied at runtime for readability')}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* === v3.5.19 — Card style (Frosted / Clear / Solid) === */}
                            <div className="space-y-2 border-t pt-4" data-testid="card-style-block">
                                <Label className="text-sm font-medium flex items-center gap-2">
                                    <Settings className="w-4 h-4" />
                                    {t('cardStyle.label', 'Welcome card style')}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    {t('cardStyle.help', 'How the welcome / feedback card looks over your wallpaper. Applied across the language gate, Review Shield AND Global Review for this card.')}
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {[
                                        { value: '', label: t('cardStyle.inheritOrg', 'Inherit'), desc: t('cardStyle.inheritOrgDesc', 'Use organization default') },
                                        { value: 'frosted', label: t('cardStyle.frosted', 'Frosted'), desc: t('cardStyle.frostedDesc', 'Translucent + blur') },
                                        { value: 'clear', label: t('cardStyle.clear', 'Clear'), desc: t('cardStyle.clearDesc', 'Wallpaper shows through') },
                                        { value: 'solid', label: t('cardStyle.solid', 'Solid'), desc: t('cardStyle.solidDesc', 'Fully opaque card') },
                                    ].map((opt) => (
                                        <button
                                            key={opt.value || 'inherit'}
                                            type="button"
                                            onClick={() => setCardStyle(opt.value)}
                                            className={`relative rounded-lg border-2 p-2 text-left transition-all ${
                                                (cardStyle || '') === opt.value
                                                    ? 'border-fuchsia-600 bg-fuchsia-50 dark:bg-fuchsia-950/30 dark:border-fuchsia-400'
                                                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                            }`}
                                            data-testid={`card-style-option-${opt.value || 'inherit'}`}
                                            aria-pressed={(cardStyle || '') === opt.value}
                                        >
                                            {/* Mini visual preview of the surface */}
                                            <div
                                                className="h-10 rounded mb-2 flex items-center justify-center text-[10px] font-medium overflow-hidden relative"
                                                style={{
                                                    background: opt.value === 'solid'
                                                        ? '#0f172a'
                                                        : 'linear-gradient(135deg,#475569 0%,#1e293b 100%)',
                                                }}
                                            >
                                                <div
                                                    className={`absolute inset-1.5 rounded text-white text-[10px] flex items-center justify-center ${
                                                        opt.value === 'frosted' ? 'bg-white/10 backdrop-blur-sm border border-white/20' :
                                                        opt.value === 'clear'   ? 'bg-white/0 border border-white/30' :
                                                        opt.value === 'solid'   ? 'bg-slate-900 border border-slate-700' :
                                                        'bg-white/10 backdrop-blur-sm border border-white/20 border-dashed'
                                                    }`}
                                                >
                                                    Aa
                                                </div>
                                            </div>
                                            <div className="text-xs font-semibold text-slate-900 dark:text-slate-100">{opt.label}</div>
                                            <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">{opt.desc}</div>
                                        </button>
                                    ))}
                                </div>
                                {/* Live preview — actual wallpaper + selected surface, updates instantly (no Save needed) */}
                                <div className="space-y-1 pt-1">
                                    <Label className="text-xs text-muted-foreground">
                                        {t('cardStyle.livePreview', 'Live preview — your actual wallpaper with the selected surface')}
                                    </Label>
                                    <LiveBrandingPreview
                                        wallpaperUrl={reviewShieldBackgroundImageUrl || orgBrand?.background_image_url || ''}
                                        zoom={orgBrand?.background_image_scale || 100}
                                        styleKey={cardStyle || orgBrand?.card_style || 'frosted'}
                                        label={cardStyle
                                            ? cardStyle.charAt(0).toUpperCase() + cardStyle.slice(1)
                                            : `${t('cardStyle.inheritOrg', 'Inherit')} → ${(orgBrand?.card_style || 'frosted')}`}
                                        testId="card-style-live-preview" />
                                    {!(reviewShieldBackgroundImageUrl || orgBrand?.background_image_url) && (
                                        <p className="text-[11px] text-muted-foreground">
                                            {t('cardStyle.livePreviewNoWall', 'No wallpaper set — guests see the default dark background.')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Show YulOnTap Branding</Label>
                                <Switch checked={showYulontapBranding} onCheckedChange={setShowYulontapBranding} />
                            </div>

                            {/* === NOTIFICATIONS === */}
                            <div className="border-t pt-4" data-testid="notification-section">
                                <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
                                    <Mail className="w-4 h-4" />
                                    {t('reviewShield.notifications', 'Notifications')}
                                </Label>
                                <p className="text-xs text-muted-foreground mb-3">{t('reviewShield.sendNotificationsTo', 'Send notifications to')}</p>

                                {/* Email chips */}
                                {(notificationEmails || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="notification-email-list">
                                        {(notificationEmails || []).map((email) => (
                                            <span key={email} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-full text-xs font-medium">
                                                {email}
                                                <button type="button" onClick={() => removeNotificationEmail(email)} className="hover:text-red-500 transition-colors ml-0.5" data-testid={`remove-email-${email}`}>
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Add email input */}
                                <div className="flex gap-2">
                                    <Input
                                        type="email"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNotificationEmail(); } }}
                                        placeholder={t('reviewShield.emailPlaceholder', 'email@example.com')}
                                        className="flex-1"
                                        data-testid="notification-email-input"
                                    />
                                    <Button type="button" variant="outline" size="sm" onClick={addNotificationEmail} data-testid="add-notification-email-btn">
                                        <Plus className="w-3.5 h-3.5 mr-1" />{t('reviewShield.addEmail', 'Add')}
                                    </Button>
                                </div>

                                {(notificationEmails || []).length === 0 && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2" data-testid="notification-fallback-msg">
                                        {t('reviewShield.noNotificationEmails', 'No recipients configured. Organization contact email will be used as fallback.')}
                                    </p>
                                )}

                                {/* SMS Notification Phones */}
                                <div className="mt-4 pt-3 border-t border-dashed">
                                    <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                        <Phone className="w-4 h-4 text-amber-500" />
                                        {t('cardSettings.smsAlertRecipients')}
                                    </Label>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        {t('cardSettings.smsAlertDesc')}
                                    </p>

                                    {(notificationPhones || []).length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {(notificationPhones || []).map(p => {
                                                const phone = recipientPhone(p);
                                                const name = recipientName(p);
                                                return (
                                                    <Badge key={phone} variant="secondary" className="gap-1 text-xs" data-testid={`notification-phone-badge-${phone}`}>
                                                        {name ? `${name} — ${phone}` : phone}
                                                        <button onClick={() => removeNotificationPhone(phone)} className="ml-1 hover:text-red-600">
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <Input
                                            type="text"
                                            value={phoneNameInput}
                                            onChange={(e) => setPhoneNameInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNotificationPhone(); } }}
                                            placeholder={t('cardSettings.smsRecipientNamePlaceholder', 'Name (optional)')}
                                            className="sm:w-36"
                                            data-testid="notification-phone-name-input"
                                        />
                                        <Input
                                            type="tel"
                                            value={phoneInput}
                                            onChange={(e) => setPhoneInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNotificationPhone(); } }}
                                            placeholder="+1 514 555 1234 or 5145551234"
                                            className="flex-1"
                                            data-testid="notification-phone-input"
                                        />
                                        <Button type="button" variant="outline" size="sm" onClick={addNotificationPhone} data-testid="add-notification-phone-btn">
                                            <Plus className="w-3.5 h-3.5 mr-1" />{t('common.add')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* === DIRECT RESPONSE COMMUNICATION === */}
                            <div className="border-t pt-4" data-testid="direct-response-section">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <Label className="text-sm font-semibold flex items-center gap-2">
                                            <Shield className="w-4 h-4 text-emerald-500" />
                                            {t('cardSettings.directResponse')}
                                        </Label>
                                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                            {t('cardSettings.directResponseDesc')}
                                        </p>
                                    </div>
                                    <Switch
                                        checked={directResponseEnabled || false}
                                        onCheckedChange={setDirectResponseEnabled}
                                        data-testid="direct-response-toggle"
                                    />
                                </div>
                            </div>

                            {/* === REVIEW QUESTIONS === */}
                            <div className="border-t pt-4" data-testid="review-questions-section">
                                <div className="flex items-center justify-between mb-1">
                                    <Label className="text-sm font-semibold flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4" />
                                        {t('reviewShield.reviewQuestions', 'Review Questions')}
                                    </Label>
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800" data-testid="questions-assigned-count">
                                        {t('reviewShield.orgWide', 'Organization-wide')}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground mb-3">{t('reviewShield.reviewQuestionsSubtitle', 'These questions are shared by all of your organization\u2019s Review Shield cards. Changes save instantly.')}</p>

                                {!showQuestionManager ? (
                                    <div>
                                        {(reviewShieldQuestions || []).length === 0 && (
                                            <p className="text-xs text-muted-foreground mb-2 italic" data-testid="no-custom-questions-msg">
                                                {t('reviewShield.noCustomQuestions', 'No custom questions assigned. Default categories will be used.')}
                                            </p>
                                        )}
                                        {/* Access to Card Settings is already gated upstream; the
                                            backend enforces org-scoped question permissions. */}
                                        <Button type="button" variant="outline" size="sm" onClick={() => setShowQuestionManager(true)} data-testid="manage-questions-btn">
                                            <Settings className="w-3.5 h-3.5 mr-1.5" />
                                            {t('reviewShield.manageQuestions', 'Manage Questions')}
                                        </Button>
                                    </div>
                                ) : (
                                    <div>
                                        <QuestionManager
                                            token={token}
                                            organizationId={selectedCard?.organization_id}
                                        />
                                        <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => setShowQuestionManager(false)}>
                                            <ChevronUp className="w-3.5 h-3.5 mr-1" />
                                            {t('reviewShield.cancel', 'Close')}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* === PUBLIC REVIEW THRESHOLD === */}
                            <div className="border rounded-lg p-3 bg-gradient-to-br from-yellow-50/50 to-amber-50/50 dark:from-yellow-950/20 dark:to-amber-950/20" data-testid="public-review-threshold-section">
                                <Label className="text-sm font-semibold flex items-center gap-2 mb-1">
                                    <Star className="w-4 h-4 text-amber-500" fill="currentColor" />
                                    {t('reviewShield.publicReviewThreshold', 'Public Review Threshold')}
                                </Label>
                                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                                    {t(
                                        'reviewShield.publicReviewThresholdDesc',
                                        'Choose the minimum star rating required before guests are invited to leave a public Google review. Ratings below this threshold will remain internal.'
                                    )}
                                </p>
                                <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Public Review Threshold">
                                    {[2, 3, 4, 5].map((n) => {
                                        const active = Number(publicReviewThreshold) === n;
                                        return (
                                            <button
                                                key={n}
                                                type="button"
                                                role="radio"
                                                aria-checked={active}
                                                onClick={() => setPublicReviewThreshold && setPublicReviewThreshold(n)}
                                                data-testid={`threshold-${n}-btn`}
                                                className={
                                                    'flex flex-col items-center justify-center gap-0.5 py-2.5 rounded-md border-2 transition-all text-xs font-semibold ' +
                                                    (active
                                                        ? 'border-amber-500 bg-amber-500 text-white shadow-sm'
                                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-amber-300')
                                                }
                                            >
                                                <span className="flex gap-0.5">
                                                    {Array.from({ length: n }, (_, i) => (
                                                        <Star
                                                            key={i}
                                                            className={active ? 'w-2.5 h-2.5' : 'w-2.5 h-2.5'}
                                                            fill="currentColor"
                                                        />
                                                    ))}
                                                </span>
                                                <span className="text-[10px] mt-0.5">
                                                    {t('reviewShield.starsCount', { count: n, defaultValue: `${n} stars` })}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                                    {t(
                                        'reviewShield.thresholdHint',
                                        'Ratings of {{n}}+ stars will show the Google review prompt; lower ratings will route to internal feedback.',
                                        { n: publicReviewThreshold }
                                    )}
                                </p>
                            </div>

                            {/* === LOYAL REVIEW — Positive-review behavior toggle === */}
                            <LoyalReviewSection
                                t={t}
                                token={token}
                                organizationId={selectedCard?.organization_id}
                                loyalReviewEnabled={!!loyalReviewEnabled}
                                loyalReviewVoucherId={loyalReviewVoucherId || ''}
                                loyalReviewAutoFlow={!!loyalReviewAutoFlow}
                                setLoyalReviewEnabled={setLoyalReviewEnabled}
                                setLoyalReviewVoucherId={setLoyalReviewVoucherId}
                                setLoyalReviewAutoFlow={setLoyalReviewAutoFlow}
                                giftGeneratorEnabled={!!giftGeneratorEnabled}
                                setGiftGeneratorEnabled={setGiftGeneratorEnabled}
                                leadGenerationOverride={leadGenerationOverride}
                                setLeadGenerationOverride={setLeadGenerationOverride}
                                giftGeneratorSetupId={giftGeneratorSetupId}
                                setGiftGeneratorSetupId={setGiftGeneratorSetupId}
                                publicReviewThreshold={publicReviewThreshold}
                            />
                        </div>
                    )}

                    {/* Positive Experience / Gift Generator — also available for
                        review-oriented modes that are NOT Review Shield, so the
                        card-level Gift Generator toggle is never hidden. */}
                    {!['review_shield', 'hotel', 'guest_assistant', 'hotel_staff'].includes(interactionMode) && (
                        <div className="mt-4">
                            <LoyalReviewSection
                                t={t}
                                token={token}
                                organizationId={selectedCard?.organization_id}
                                loyalReviewEnabled={!!loyalReviewEnabled}
                                loyalReviewVoucherId={loyalReviewVoucherId || ''}
                                loyalReviewAutoFlow={!!loyalReviewAutoFlow}
                                setLoyalReviewEnabled={setLoyalReviewEnabled}
                                setLoyalReviewVoucherId={setLoyalReviewVoucherId}
                                setLoyalReviewAutoFlow={setLoyalReviewAutoFlow}
                                giftGeneratorEnabled={!!giftGeneratorEnabled}
                                setGiftGeneratorEnabled={setGiftGeneratorEnabled}
                                leadGenerationOverride={leadGenerationOverride}
                                setLeadGenerationOverride={setLeadGenerationOverride}
                                giftGeneratorSetupId={giftGeneratorSetupId}
                                setGiftGeneratorSetupId={setGiftGeneratorSetupId}
                                publicReviewThreshold={publicReviewThreshold}
                            />
                        </div>
                    )}

                    {/* === TIMING SYSTEM (all modes — destination-only override) === */}
                    <TimingPanel
                        timingEnabled={timingEnabled}
                        setTimingEnabled={setTimingEnabled}
                        timingSchedule={timingSchedule}
                        setTimingSchedule={setTimingSchedule}
                        selectedCard={selectedCard}
                        t={t}
                    />

                    {/* === EMPLOYEE PIN PROTECTION (all modes) === */}
                    <div className="border-t pt-4" data-testid="employee-pin-section">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <Label className="text-sm font-semibold flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-orange-500" />
                                    {t('cardSettings.requireEmployeePin')}
                                </Label>
                                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                    {t('cardSettings.requireEmployeePinDesc')}
                                </p>
                            </div>
                            <Switch
                                checked={requireEmployeePin}
                                onCheckedChange={setRequireEmployeePin}
                                data-testid="require-employee-pin-toggle"
                            />
                        </div>
                    </div>

                    {/* === TAP PROTECTION (all modes) — P_TAP_PROTECTION_1.0 === */}
                    <TapProtectionSection
                        t={t}
                        interactionMode={interactionMode}
                        landingPageEnabled={landingPageEnabled}
                        tapProtectionEnabled={
                            tapProtectionEnabled !== null ? tapProtectionEnabled : (reviewCounterEnabled ?? true)
                        }
                        duplicateProtectionEnabled={
                            duplicateProtectionEnabled !== null ? duplicateProtectionEnabled : (reviewCounterEnabled ?? true)
                        }
                        duplicateWindowSeconds={duplicateWindowSeconds ?? 30}
                        dwellProtectionEnabled={
                            dwellProtectionEnabled !== null ? dwellProtectionEnabled : (reviewCounterEnabled ?? true)
                        }
                        dwellThresholdSeconds={dwellThresholdSeconds ?? 10}
                        testMode={testMode}
                        setTestMode={setTestMode}
                        setTapProtectionEnabled={(v) => {
                            setTapProtectionEnabled(v);
                            setReviewCounterEnabled?.(v); // keep legacy master toggle in sync
                        }}
                        setDuplicateProtectionEnabled={setDuplicateProtectionEnabled}
                        setDuplicateWindowSeconds={setDuplicateWindowSeconds}
                        setDwellProtectionEnabled={setDwellProtectionEnabled}
                        setDwellThresholdSeconds={setDwellThresholdSeconds}
                    />

                    {/* === GIFT CARD / VOUCHER TOGGLE (all modes) === */}
                    <div className="border-t pt-4" data-testid="gift-card-section">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <Label className="text-sm font-semibold flex items-center gap-2">
                                    <Gift className="w-4 h-4 text-amber-500" />
                                    {t('cardSettings.enableGiftCards')}
                                </Label>
                                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                    {t('cardSettings.enableGiftCardsDesc')}
                                </p>
                            </div>
                            <Switch
                                checked={giftCardEnabled || false}
                                onCheckedChange={setGiftCardEnabled}
                                data-testid="gift-card-toggle"
                            />
                        </div>
                    </div>

                </div>

                <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2 flex-col sm:flex-row gap-2">
                    {/*
                      v4.34.1 — Show "Apply to Multiple" for ALL interaction
                      modes (Standard Review, Review Shield, Advertising,
                      Global Review). The backend `/cards/bulk-landing-settings`
                      endpoint already sends and persists `interaction_mode`
                      across modes; previously the gate only allowed
                      advertising/review_shield/global_review, which left
                      operators no way to bulk-apply Standard Review.
                    */}
                    {isSuperAdmin() && (
                        <Button variant="outline" onClick={() => setShowBulkApplyModal(true)} className="sm:mr-auto" data-testid="bulk-apply-btn">
                            <Copy className="w-4 h-4 mr-2" />{t('cardSettings.applyToMultiple')}
                        </Button>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {(isDirty || promoDirty) && (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mr-1 self-center underline decoration-dotted underline-offset-4 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                                        data-testid="card-settings-dirty-indicator"
                                        aria-label={t('cardSettings.unsavedChanges', 'Unsaved changes')}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                        {t('cardSettings.unsavedChanges', 'Unsaved changes')}
                                        {changesDiff.length > 0 && (
                                            <span className="ml-1 text-[10px] bg-amber-500/20 text-amber-600 dark:text-amber-400 px-1.5 rounded-full">{changesDiff.length}</span>
                                        )}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    side="top"
                                    align="start"
                                    className="w-80 p-0"
                                    data-testid="card-settings-changes-preview"
                                >
                                    <div className="px-4 py-3 border-b">
                                        <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">{t('manager.changesPreviewTitle', 'Pending Changes')}</p>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">{t('manager.changesPreviewHint', 'These changes will apply once you tap Save')}</p>
                                    </div>
                                    <ul className="max-h-64 overflow-y-auto divide-y" data-testid="card-settings-changes-list">
                                        {changesDiff.length === 0 ? (
                                            <li className="px-4 py-3 text-xs text-muted-foreground text-center">—</li>
                                        ) : changesDiff.map((c) => (
                                            <li key={c.key} className="px-4 py-2.5" data-testid={`card-settings-change-${c.key}`}>
                                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.label}</p>
                                                <div className="flex items-center gap-2 mt-1 text-sm">
                                                    <span className="line-through text-muted-foreground truncate max-w-[120px]">{c.before}</span>
                                                    <span className="text-muted-foreground">→</span>
                                                    <span className="font-medium text-emerald-600 dark:text-emerald-400 truncate max-w-[120px]">{c.after}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </PopoverContent>
                            </Popover>
                        )}
                        <Button
                            onClick={handleSaveAll}
                            disabled={(!isDirty && !promoDirty) || isSaving}
                            data-testid="save-landing-settings-btn"
                            className="min-h-[40px]"
                        >
                            {isSaving ? t('common.saving', 'Saving…') : t('common.saveChanges', 'Save Changes')}
                        </Button>
                        {interactionMode === 'advertising' && (
                            <Button variant="outline" onClick={() => window.open(`/landing/${selectedCard?.card_id}`, '_blank', 'width=500,height=800,noopener,noreferrer')} data-testid="preview-footer-btn">
                                <ExternalLink className="w-4 h-4 mr-2" />{t('cardSettings.preview')}
                            </Button>
                        )}
                        {interactionMode === 'review_shield' && (
                            <Button variant="outline" onClick={async () => {
                                try {
                                    await handleSaveLandingSettings();
                                    setTimeout(() => {
                                        window.open(`/review-shield/${selectedCard?.card_id}`, '_blank', 'width=420,height=750,noopener,noreferrer');
                                    }, 300);
                                } catch { 
                                    window.open(`/review-shield/${selectedCard?.card_id}`, '_blank', 'width=420,height=750,noopener,noreferrer');
                                }
                            }} data-testid="preview-review-shield-btn">
                                <ExternalLink className="w-4 h-4 mr-2" />{t('cardSettings.preview')}
                            </Button>
                        )}
                        {globalReviewEnabled && interactionMode !== 'review_shield' && (
                            <Button variant="outline" onClick={async () => {
                                try {
                                    await handleSaveLandingSettings();
                                    setTimeout(() => {
                                        window.open(`/global-review/${selectedCard?.card_id}`, '_blank', 'width=420,height=750,noopener,noreferrer');
                                    }, 300);
                                } catch {
                                    window.open(`/global-review/${selectedCard?.card_id}`, '_blank', 'width=420,height=750,noopener,noreferrer');
                                }
                            }} data-testid="preview-global-review-btn">
                                <Globe className="w-4 h-4 mr-2" />{t('cardSettings.previewGlobalReview')}
                            </Button>
                        )}
                        <Button
                            variant="secondary"
                            onClick={() => { onOpenChange(false); }}
                            data-testid="card-settings-close-btn"
                        >
                            {isDirty ? t('common.cancel') : t('cardSettings.done')}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
